/* 一般外科 LEVEL 2 — 急診照會：腸胃炎？（strangulated right femoral hernia → SBO; trap: taxis） */
import {
  addWait,
  ask,
  C,
  type Cell,
  chat,
  type Choice,
  type ChoiceItem,
  clearWait,
  clock,
  closeDialog,
  closeZoom,
  type Ctx,
  dither,
  doc,
  DRAW,
  drawEcg,
  ent,
  eventBanner,
  finding,
  flag,
  getImagery,
  type Hotspot,
  incomingCall,
  type Level,
  leaveBy,
  me,
  mkEnt,
  note,
  obox,
  openDialog,
  openMonitor,
  openZoom,
  pell,
  pipSet,
  playEnding,
  pline,
  post,
  ptext,
  R,
  rebuildDyn,
  session,
  setZBar,
  spawnAt,
  spend,
  strList,
  T,
  tr,
  walkEnt,
  type ZoomCfg,
} from "../../engine";
import { photoOnly, sideMark } from "../shared/imaging";
import { type BedAnim, bedAnimPos, bedHots, drawBedScene } from "../shared/bedside";
import { isTripUrgency, startVsTrip, type TripUrgency, type VsTrip } from "../shared/vs-trip";

/* ---------- static data ---------- */

/** What 秀琴阿嬤 (and 美華) can tell; the model reports ids it revealed. */
const FACTS = {
  onset: tr(
    "前天晚上吃了鄰居包的粽子，昨天下午開始肚臍周圍一陣一陣絞痛",
    "Two nights ago she ate rice dumplings (zongzi) a neighbor made; since yesterday afternoon, cramping pain around the belly button that comes in waves",
  ),
  vomit: tr(
    "昨晚開始吐，一開始是吃的東西，後來吐綠綠苦苦的水，今天吐了六七次，最後一次有點臭",
    "Started vomiting last night: food at first, then bitter green fluid; six or seven times today, and the last one smelled a bit foul",
  ),
  bowel: tr("昨天早上之後就沒有放屁、也沒有大便", "No gas and no bowel movement since yesterday morning"),
  intake: tr("昨天中午以後都沒吃，喝水也會吐", "Hasn't eaten since yesterday noon; even water comes back up"),
  urine: tr("今天尿很少，顏色很深", "Very little urine today, and it's very dark"),
  chills: tr(
    "下午開始覺得冷、發抖，蓋兩條被子",
    "Since this afternoon she feels cold and shivers, even under two blankets",
  ),
  pmh: tr("高血壓吃藥（amlodipine），膝蓋退化", "Hypertension on medication (amlodipine); knee osteoarthritis"),
  surgery: tr("從來沒有開過刀，兩個小孩都是自然產", "Has never had any surgery; both children were vaginal deliveries"),
  meds: tr(
    "每天一顆阿斯匹靈（醫生說顧血管），膝蓋痛會自己吃止痛藥",
    "One aspirin a day (the doctor said it's good for her blood vessels); takes painkillers on her own when her knees hurt",
  ),
  allergy: tr("沒有藥物過敏", "No drug allergies"),
  lump: tr(
    "右邊大腿根（鼠蹊部下面）有一顆「肉球」二十幾年了，躺下來或用手推就會回去",
    'For twenty-some years she\'s had a "lump of flesh" at the top of her right thigh (just below the groin); it used to go back in when she lay down or pushed on it',
  ),
  stuck: tr(
    "從昨天下午開始那顆推不回去，變硬、很痛，今天皮膚紅紅的",
    "Since yesterday afternoon it won't go back in; it's hard and very painful, and today the skin over it is red",
  ),
  hid: tr(
    "她叫女兒不要跟醫生講那顆，覺得丟臉，也怕要開刀",
    "She told her daughter not to mention the lump to the doctors; she's embarrassed and afraid she'll need surgery",
  ),
  worry: tr(
    "年紀這麼大了不想開刀，怕拖累孩子",
    "At her age she doesn't want surgery; she's afraid of being a burden to her children",
  ),
} as const;
type FactId = keyof typeof FACTS;
const isFact = (x: string): x is FactId => x in FACTS;

/** Order id → [label, minutes until ready]. */
const ORD = {
  lactate: [tr("VBG＋Lactate", "VBG + lactate"), 10],
  crp: ["CRP", 30],
  coag: [tr("PT／aPTT", "PT/aPTT"), 30],
  ts: [tr("血型與抗體篩檢（備血）", "Type and screen (crossmatch)"), 40],
  ecg: [tr("12 導程心電圖", "12-lead ECG"), 4],
  cxr: [tr("胸部 X 光（床邊）", "Portable CXR"), 20],
  ct: [tr("腹部 CT（含顯影劑）", "CT abd/pelvis w/ contrast"), 45],
  iv2: [tr("第二條 18G 留置針", "Second IV, 18G"), 5],
  fluid: [tr("LR 1000 mL 快速輸注", "LR 1000 mL bolus"), 5],
  kcl: [tr("補鉀：KCl 20 mEq 加入輸液", "K replacement: KCl 20 mEq in IV fluids"), 5],
  npo: [tr("禁食禁水", "NPO"), 1],
  ng: [tr("放鼻胃管（請阿芬放）", "NG tube (Fen places it)"), 12],
  foley: [tr("放導尿管、記尿量", "Foley, track urine output"), 8],
  abx: [tr("Ceftriaxone 2 g＋Metronidazole 500 mg IV", "Ceftriaxone 2 g + metronidazole 500 mg IV"), 6],
  analgesic: ["Morphine 2 mg IV", 4],
  o2: [tr("氧氣鼻導管 3 L/min", "O2 nasal cannula 3 L/min"), 2],
} as const satisfies Record<string, readonly [string, number]>;
type OrderId = keyof typeof ORD;
const isOrder = (x: string): x is OrderId => x in ORD;

/** Results the ER already had before the consult. */
const INIT_LABS = tr(
  "21:50 急診已驗：WBC 13,800（Seg 88%），Hb 13.9，PLT 210k；Na 132，K 3.2，Cl 94，BUN 38，Cr 1.4，Glucose 156；Lipase 32；AST／ALT 正常",
  "21:50 ER labs: WBC 13,800 (Seg 88%), Hb 13.9, PLT 210k; Na 132, K 3.2, Cl 94, BUN 38, Cr 1.4, glucose 156; lipase 32; AST/ALT normal",
);

/** Self-placed NG tube steps, in the right order. */
const NG_STEPS = {
  explain: tr("跟阿嬤解釋、取得同意", "Explain to Grandma, get consent"),
  sit: tr("床頭搖高，讓她坐起來", "Raise the head of the bed, sit her up"),
  measure: tr("量長度：鼻尖→耳垂→劍突", "Measure: nose → earlobe → xiphoid"),
  lube: tr("潤滑管子前端", "Lubricate the tip"),
  insert: tr("從鼻孔放進去", "Insert through the nostril"),
  swallow: tr("請她吞口水，順勢往下推", "Have her swallow, advance with it"),
  advance: tr("推到標記處", "Advance to the mark"),
  confirm: tr("反抽＋打氣聽診，確認位置", "Aspirate + air and listen to confirm"),
  tape: tr("膠布固定在鼻子", "Tape it to the nose"),
  bag: tr("接上引流袋", "Connect the drainage bag"),
} as const;
type NgStep = keyof typeof NG_STEPS;
const NG_ORDER = Object.keys(NG_STEPS) as NgStep[];

interface Vitals {
  sbp: number;
  dbp: number;
  hr: number;
  rr: number;
  spo2: number;
  temp: number;
}
interface Order {
  at: number;
  ready: number;
  done: boolean;
}
interface NgProc {
  done: NgStep[];
  /** 0 none · 1 in the nose · 2 pharynx · 3 at the mark. */
  depth: number;
  /** Tube went into the airway / coiled; must pull back. */
  wrong: boolean;
}
type PtLoc = "bed" | "ct";
type EndKind = "A" | "B" | "C";

interface Vars {
  revealed: Set<FactId>;
  orders: Partial<Record<OrderId, Order>>;
  results: { t: number; text: string }[];
  vitalsSeen: { t: number; v: Vitals }[];
  kub: Set<string>;
  ctSeen: Set<string>;
  curtain: boolean;
  chap: boolean;
  chapAsked: boolean;
  chapLeaveAt: number | null;
  consent: boolean;
  groinAt: number | null;
  rectal: boolean;
  taxisAt: number | null;
  taxisTold: boolean;
  erPushed: boolean;
  ngAt: number | null;
  ngBy: "self" | "nurse" | null;
  ng: NgProc | null;
  headUp: boolean;
  aspAt: number | null;
  aspMild: boolean;
  aspActs: string[];
  fluidAt: number | null;
  abxAt: number | null;
  ptLoc: PtLoc;
  ctLeave: number | null;
  ctBack: number | null;
  peritAt: number | null;
  lastVomit: number;
  dauHere: boolean;
  explained: boolean;
  agreed: boolean;
  vsTalked: boolean;
  vsHinted: boolean;
  vsAccepted: boolean;
  vsAcceptedAt: number | null;
  vsArriveAt: number | null;
  vsHere: boolean;
  trip: VsTrip | null;
  vsCbAt: number | null;
  rCalled: boolean;
  rComingAt: number | null;
  rHere: boolean;
  dispoAt: number | null;
  bedAnim: BedAnim | null;
  bedGone: boolean;
  endAt: number | null;
}

const S = session<Vars>();
const DOOR: Cell = [15, 11];
const PT = "xiuqin";

/* ---------- patient state ---------- */

/** Minute peritonitis sets in: dead bowel, faster if pushed back into the belly. */
const peritThreshold = (): number => Math.min(58, S.taxisAt !== null ? S.taxisAt + 16 : 999);

/** Minutes since peritonitis began, or null. */
const perit = (): number | null => (S.peritAt === null ? null : S.t - S.peritAt);

function vitals(): Vitals {
  const t = S.t;
  const fl = S.fluidAt !== null ? Math.min(1, (t - S.fluidAt) / 10) : 0;
  let temp = 37.7 + 0.008 * t;
  let hr = 104 + 0.28 * t - 9 * fl;
  let sbp = 134 - 0.25 * t + 9 * fl;
  let dbp = 78 - 0.15 * t + 4 * fl;
  let rr = 20 + (t > 40 ? 2 : 0);
  let spo2 = 97;
  const p = perit();
  if (p !== null) {
    hr += 14 + p * 0.5;
    sbp -= 14 + p * 0.7;
    dbp -= 8 + p * 0.3;
    temp += 0.7;
    rr += 4;
  }
  if (S.aspAt !== null) {
    spo2 = S.orders.o2 ? 94 : S.aspMild ? 92 : 88;
    rr += S.aspMild ? 3 : 6;
    hr += 6;
  }
  return {
    sbp: Math.round(sbp),
    dbp: Math.round(dbp),
    hr: Math.round(hr),
    rr,
    spo2,
    temp: Math.round(temp * 10) / 10,
  };
}

const vtxt = (v: Vitals): string =>
  tr(
    `BP ${v.sbp}/${v.dbp}，HR ${v.hr}，RR ${v.rr}，SpO₂ ${v.spo2}%，體溫 ${v.temp.toFixed(1)}°C`,
    `BP ${v.sbp}/${v.dbp}, HR ${v.hr}, RR ${v.rr}, SpO₂ ${v.spo2}%, temp ${v.temp.toFixed(1)}°C`,
  );

function lactate(t: number): number {
  const p = S.peritAt !== null && t >= S.peritAt ? t - S.peritAt : null;
  let l = 2.6 + 0.025 * t + (p !== null ? 1.6 + 0.05 * p : 0);
  if (S.fluidAt !== null && t > S.fluidAt + 10) l -= 0.4;
  return Math.round(l * 10) / 10;
}

const ngOut = (): number => (S.ngAt === null ? 0 : 420 + 6 * (S.t - S.ngAt));

function condition(): string {
  const p = perit();
  if (p !== null)
    return tr(
      "肚子變硬、痛到不敢動，講話很喘很小聲，一直說好痛",
      "her belly has gone hard; it hurts too much to move; she speaks breathlessly in a tiny voice and keeps saying how much it hurts",
    );
  if (S.aspAt !== null && S.t - S.aspAt < 10)
    return tr("剛吐完嗆到，一直咳、很喘", "she just vomited and choked on it; coughing nonstop, short of breath");
  if (S.taxisAt !== null && S.t - S.taxisAt < 14)
    return tr(
      "剛剛那顆被推進去之後覺得好像比較不痛了，有點放心",
      "since the lump was pushed back in it seems to hurt less, and she's a little relieved",
    );
  if (S.t < 20)
    return tr(
      "肚子一陣一陣痛，很虛弱，但很客氣、怕麻煩別人",
      "cramping belly pain in waves, very weak, but very polite and afraid of troubling anyone",
    );
  if (S.t < 40)
    return tr("越來越不舒服，嘴巴很乾，一直想吐", "feeling worse and worse, mouth very dry, keeps wanting to throw up");
  return tr("很疲倦，發冷，一陣一陣痛得縮起來", "exhausted, chilled, curling up with each wave of pain");
}

const atBed = (): boolean => S.ptLoc === "bed";

function known(): string {
  const out: string[] = [];
  out.push(
    tr(
      `看過的生命徵象：${S.vitalsSeen.map((x) => `${clock(x.t)} ${vtxt(x.v)}`).join("；") || "沒有自己看過（分診 BP 138/80，HR 102，37.6°C）"}`,
      `Vitals seen: ${S.vitalsSeen.map((x) => `${clock(x.t)} ${vtxt(x.v)}`).join("; ") || "none checked personally (triage BP 138/80, HR 102, 37.6°C)"}`,
    ),
  );
  out.push(tr(`急診已有的抽血：${INIT_LABS}`, `Labs the ER already drew: ${INIT_LABS}`));
  out.push(
    tr(
      `KUB：${S.kub.size ? [...S.kub].join("；") : "沒看"}`,
      `KUB: ${S.kub.size ? [...S.kub].join("; ") : "not reviewed"}`,
    ),
  );
  out.push(
    tr(
      `檢查發現：${S.findings.map((f) => `${clock(f.t)} ${f.name}：${f.text}`).join("；") || "無"}`,
      `Exam findings: ${S.findings.map((f) => `${clock(f.t)} ${f.name}: ${f.text}`).join("; ") || "none"}`,
    ),
  );
  const pend = (Object.entries(S.orders) as [OrderId, Order][]).filter(([, o]) => !o.done).map(([id]) => ORD[id][0]);
  const done = (Object.entries(S.orders) as [OrderId, Order][]).filter(([, o]) => o.done).map(([id]) => ORD[id][0]);
  out.push(
    tr(
      `已完成的醫囑：${done.join("、") || "無"}；等待中：${pend.join("、") || "無"}`,
      `Orders completed: ${done.join(", ") || "none"}; pending: ${pend.join(", ") || "none"}`,
    ),
  );
  out.push(
    tr(
      `問出：${[...S.revealed].map((id) => FACTS[id]).join("；") || "幾乎沒問出什麼"}`,
      `History obtained: ${[...S.revealed].map((id) => FACTS[id]).join("; ") || "almost nothing"}`,
    ),
  );
  out.push(
    tr(
      `鼻胃管：${S.ngAt !== null ? `${clock(S.ngAt)} 放好（${S.ngBy === "self" ? "clerk 自己放" : "阿芬放"}），引流 ${ngOut()} mL 暗綠色` : "沒放"}`,
      `NG tube: ${S.ngAt !== null ? `placed at ${clock(S.ngAt)} (${S.ngBy === "self" ? "by the clerk" : "by Fen"}), ${ngOut()} mL dark green output` : "not placed"}`,
    ),
  );
  if (S.taxisAt !== null)
    out.push(
      tr(
        `clerk 在 ${clock(S.taxisAt)} 用手把鼠蹊部的團塊推回去（taxis）`,
        `At ${clock(S.taxisAt)} the clerk manually pushed the groin lump back in (taxis)`,
      ),
    );
  if (S.aspAt !== null)
    out.push(
      tr(
        `${clock(S.aspAt)} 病人嘔吐嗆到；clerk 的處置：${S.aspActs.join("、") || "無"}`,
        `${clock(S.aspAt)} the patient vomited and aspirated; clerk's response: ${S.aspActs.join(", ") || "none"}`,
      ),
    );
  return out.join("\n");
}

/* ---------- monitor ---------- */

function openMon(): void {
  if (!atBed()) {
    note(tr("阿嬤去做 CT 了，監視器是空的。", "Grandma is off at CT; the monitor is blank."), "bed");
    return;
  }
  const v = vitals();
  S.vitalsSeen.push({ t: S.t, v });
  post("bed", "result", null, vtxt(v), { silent: true });
  openMonitor(
    tr("15 床・監視器", "Bed 15 · Monitor"),
    () => {
      const now = vitals();
      const last = S.vitalsSeen.at(-1) ?? { t: S.t, v: now };
      return {
        hr: now.hr,
        spo2: now.spo2,
        rr: now.rr,
        sbp: last.v.sbp,
        dbp: last.v.dbp,
        nibpT: clock(last.t),
        alarm: now.hr > 118 || now.spo2 < 92,
      };
    },
    [
      C(tr("重新量 NIBP（1 分鐘）", "Recheck NIBP (1 min)"), () => {
        const nv = vitals();
        S.vitalsSeen.push({ t: S.t, v: nv });
        post("bed", "result", null, vtxt(nv));
        spend(1);
      }),
      C(tr("量耳溫", "Ear temp"), () => {
        post("bed", "result", null, tr(`耳溫 ${vitals().temp.toFixed(1)}°C`, `Ear temp ${vitals().temp.toFixed(1)}°C`));
        spend(1);
      }),
    ],
    () => {
      if (S.dialog !== "bed") return;
      const z = bedZoom();
      if (z) openZoom({ dlg: "bed", ...z });
    },
  );
  spend(1);
}

/* ---------- abdominal exam ---------- */

function findOnce(label: string, name: string, txt: string, cost = 1): void {
  if (S.findings.some((f) => f.name === name && f.text === txt)) {
    note(tr(`剛剛檢查過了：${txt}`, `Already checked: ${txt}`), "bed");
    return;
  }
  me("bed", tr(`（${label}）`, `(${label})`));
  finding(name, txt);
  post("bed", "narr", null, txt);
  spend(cost);
}

const abd = {
  look: () =>
    findOnce(
      tr("看肚子", "Inspects the abdomen"),
      tr("腹部視診", "Abdominal inspection"),
      S.ngAt !== null
        ? tr(
            "肚子還是脹，但比剛剛軟一點。沒有任何手術疤痕。",
            "Still distended, but a little softer than before. No surgical scars anywhere.",
          )
        : tr(
            "肚子明顯脹起來，像小鼓。仔細看：肚皮上沒有任何手術疤痕。",
            "Markedly distended, tight as a little drum. Look closely: no surgical scars anywhere on the abdomen.",
          ),
    ),
  listen: () =>
    findOnce(
      tr("聽腸音", "Listens for bowel sounds"),
      tr("腸音", "Bowel sounds"),
      perit() !== null
        ? tr("腸音變得很少，幾乎聽不到。", "Bowel sounds are now sparse, almost absent.")
        : tr(
            "腸音高亢，一陣一陣出現金屬般的叮叮聲（tinkling），跟她絞痛的時間一致。",
            "High-pitched bowel sounds with bursts of metallic tinkling that line up with her cramps.",
          ),
      2,
    ),
  percuss: () =>
    findOnce(tr("叩診", "Percusses"), tr("叩診", "Percussion"), tr("整個肚子鼓音。", "Tympanic throughout."), 1),
  palpate: () => {
    if (!atBed()) return;
    const p = perit();
    findOnce(
      tr("觸診", "Palpates"),
      tr("腹部觸診", "Abdominal palpation"),
      p !== null
        ? tr(
            "一碰肚子她就大叫。右下腹明顯壓痛、反彈痛，整片肌肉僵硬（guarding）。",
            "She cries out the moment you touch her belly. Marked RLQ tenderness with rebound, and diffuse rigid guarding.",
          )
        : S.taxisAt !== null
          ? tr(
              "肚子脹，肚臍周圍輕壓痛，右下腹比剛剛更敏感，還沒有反彈痛。",
              "Distended, mild periumbilical tenderness; the RLQ is more tender than before. No rebound yet.",
            )
          : tr(
              "肚子脹，肚臍周圍輕度壓痛，沒有反彈痛、沒有肌肉僵硬。",
              "Distended, mild periumbilical tenderness. No rebound, no guarding.",
            ),
      2,
    );
  },
};

/* ---------- privacy: curtain, chaperone, consent ---------- */

function toggleCurtain(): void {
  S.curtain = !S.curtain;
  me("bed", S.curtain ? tr("（拉上簾子）", "(Draws the curtain)") : tr("（拉開簾子）", "(Opens the curtain)"));
  post(
    "bed",
    "narr",
    null,
    S.curtain
      ? tr(
          "你把 15 床的簾子拉起來。外面的吵雜聲小了一點。",
          "You draw the curtain around Bed 15. The noise outside drops a little.",
        )
      : tr("簾子拉開了。", "The curtain is open."),
  );
}

function askChaperone(): void {
  if (S.chap || S.chapAsked) {
    note(
      S.chap
        ? tr("阿芬已經在床邊了。", "Fen is already at the bedside.")
        : tr("阿芬說她馬上過來。", "Fen says she’ll be right over."),
      "bed",
    );
    return;
  }
  S.chapAsked = true;
  me("nurse", tr("（請阿芬陪同鼠蹊部檢查）", "(Asks Fen to chaperone the groin exam)"));
  post(
    "nurse",
    "npc",
    "afen",
    tr("好，等我一下，我把這袋水掛完就過去。", "Sure, give me a sec. Let me hang this bag and I’ll be over."),
  );
  const a = ent("afen");
  walkEnt(a, [3, 2]);
  a.onArrive = () => {
    S.chap = true;
    S.chapLeaveAt = S.t + 12;
    a.face = "left";
    post(
      "bed",
      "npc",
      "afen",
      tr(
        "阿嬤，我是護理師，我陪妳喔。醫師要看一下，不會很久。",
        "Grandma, I’m the nurse. I’ll stay right here with you. The doctor just needs a quick look, it won’t take long.",
      ),
    );
    rebuildDyn();
  };
  spend(1);
}

function explainExam(): void {
  if (!atBed()) return;
  me(
    "bed",
    tr(
      "（跟阿嬤解釋：吐成這樣要找原因，需要看一下鼠蹊部和大腿根，會蓋好其他地方）",
      "(Explains to Grandma: with this much vomiting we need to find the cause, so you need to check her groin and the top of her thighs, and you’ll keep everything else covered)",
    ),
  );
  if (!S.curtain) {
    post("bed", "npc", PT, tr("在這裡喔？外面都是人捏……", "Right here? There are people everywhere out there…"));
    spend(1);
    return;
  }
  S.consent = true;
  post(
    "bed",
    "npc",
    PT,
    S.chap
      ? tr("……好啦。有護士小姐在，我比較放心。", "…All right. With the nurse here, I feel better about it.")
      : tr(
          "……要看那裡喔？歹勢……那可以叫個護士小姐來陪嗎？",
          "…You have to look down there? Oh, how embarrassing… Could you ask a nurse to come stay with me?",
        ),
  );
  spend(1);
}

/** Why the groin can't be examined yet, or null. */
function groinBlock(): string | null {
  if (!atBed()) return tr("阿嬤不在床上。", "Grandma isn’t in her bed.");
  if (!S.curtain)
    return tr(
      "阿嬤拉住被子：「這裡外面都是人捏……你要看哪裡？」",
      "Grandma clutches the blanket: “There are people all around out here… What are you trying to look at?”",
    );
  if (!S.consent)
    return tr(
      "阿嬤縮了一下：「欸，你要做什麼？」——先跟她解釋為什麼要看、取得她同意。",
      "Grandma flinches: “Hey, what are you doing?” Explain why you need to look and get her consent first.",
    );
  if (!S.chap)
    return tr(
      "阿嬤：「可以叫個護士小姐來陪嗎？」——請阿芬來陪同。",
      "Grandma: “Could you get a nurse to stay with me?” Ask Fen to chaperone.",
    );
  return null;
}

function tryGroin(): void {
  const why = groinBlock();
  if (why) {
    post("bed", "narr", null, why);
    return;
  }
  openZoom({ dlg: "bed", ...groinZoom() });
}

function rectal(): void {
  const why = groinBlock();
  if (why) {
    post("bed", "narr", null, why);
    return;
  }
  if (S.rectal) {
    note(tr("剛剛做過肛門指診了。", "You already did the rectal exam."), "bed");
    return;
  }
  S.rectal = true;
  me("bed", tr("（肛門指診）", "(Rectal exam)"));
  finding(
    tr("肛門指診", "Rectal exam"),
    tr("直腸裡沒有糞便，指套沒有血。", "No stool in the rectum; no blood on the glove."),
  );
  post(
    "bed",
    "narr",
    null,
    tr("直腸是空的，沒有糞便，指套上沒有血。", "The rectal vault is empty: no stool, no blood on the glove."),
  );
  spend(2);
}

/* ---------- groin exam & the taxis trap ---------- */

function groinRight(): void {
  if (S.taxisAt !== null) {
    findOnce(
      tr("摸右側鼠蹊部", "Palpates the right groin"),
      tr("右側鼠蹊部", "Right groin"),
      tr(
        "原本那顆硬塊摸不太到了，但右側腹股溝韌帶下方的皮膚還是紅、熱，一壓她就縮。",
        "The hard lump is barely palpable now, but the skin below the right inguinal ligament is still red and hot, and she flinches when you press.",
      ),
      1,
    );
    return;
  }
  const first = S.groinAt === null;
  findOnce(
    tr("看、摸右側鼠蹊部", "Inspects and palpates the right groin"),
    tr("右側鼠蹊部", "Right groin"),
    tr(
      "右側腹股溝韌帶下方、恥骨結節外側，一顆約 3×3 公分的硬塊。表面皮膚發紅、溫熱，壓痛非常明顯，輕推推不回去。",
      "Below the right inguinal ligament, lateral to the pubic tubercle: a firm mass about 3 × 3 cm. The overlying skin is red and warm, it is exquisitely tender, and gentle pressure does not reduce it.",
    ),
    2,
  );
  if (first) {
    S.groinAt = S.t;
    post(
      "bed",
      "npc",
      PT,
      tr(
        "唉唷……就是那顆啦。以前躺下來就會回去，昨天開始就卡住了……",
        "Aiyo… that’s the one. It used to go back in when I lay down, but since yesterday it’s been stuck…",
      ),
    );
    S.revealed.add("lump");
    S.revealed.add("stuck");
  }
}

function cough(): void {
  if (S.taxisAt !== null) {
    note(tr("團塊已經摸不到了。", "You can’t feel the lump anymore."), "bed");
    return;
  }
  findOnce(
    tr("請她咳嗽，摸團塊", "Has her cough, feels the lump"),
    tr("咳嗽衝擊", "Cough impulse"),
    tr(
      "請阿嬤咳嗽：團塊沒有隨咳嗽傳來衝擊感，也沒有變大或縮小。",
      "You ask Grandma to cough: no cough impulse in the lump, and it doesn’t grow or shrink.",
    ),
    1,
  );
}

function taxis(): void {
  if (S.taxisAt !== null) {
    note(tr("已經推過了。", "You already pushed it back."), "bed");
    return;
  }
  S.taxisAt = S.t;
  me(
    "bed",
    tr(
      "（用手掌包住團塊，持續施壓，試著把它推回腹腔）",
      "(Cups the lump in your palm and applies steady pressure, trying to push it back into the abdomen)",
    ),
  );
  post("bed", "npc", PT, tr("唉唷——！痛痛痛……", "Aiyo! Ow, ow, ow…"));
  post(
    "bed",
    "narr",
    null,
    tr(
      "你持續壓了快一分鐘。突然手底下「噗」一下，團塊好像縮回去了，摸起來小了很多。",
      "You keep pressing for nearly a minute. Suddenly something gives under your hand with a soft “pop”; the lump seems to slip back in and feels much smaller.",
    ),
  );
  post("bed", "npc", PT, tr("……咦？好像有比較不痛了……", "…Oh? I think it hurts a little less…"));
  finding(
    tr("徒手推回（taxis）", "Manual reduction (taxis)"),
    tr("鼠蹊部團塊被推回去，表面皮膚仍紅腫", "Groin lump pushed back in; the overlying skin is still red and swollen"),
  );
  closeZoom();
  spend(2);
}

/* ---------- self-placed NG tube ---------- */

function ngStep(s: NgStep): void {
  const p = S.ng;
  if (!p || S.ngAt !== null) return;
  const has = (x: NgStep): boolean => p.done.includes(x);
  me("bed", tr(`（${NG_STEPS[s]}）`, `(${NG_STEPS[s]})`));
  if (p.wrong) {
    post(
      "bed",
      "alert",
      "afen",
      tr("她一直咳、臉都發紫了！先拉出來！", "She can’t stop coughing and she’s turning blue! Pull it out first!"),
    );
    return;
  }
  switch (s) {
    case "explain":
      post(
        "bed",
        "npc",
        PT,
        tr(
          "要從鼻子插管子喔……好啦，吐成這樣也很艱苦。",
          "A tube up my nose… oh, all right. Throwing up like this is miserable too.",
        ),
      );
      break;
    case "sit":
      S.headUp = true;
      post(
        "bed",
        "narr",
        null,
        tr("床頭搖到 60 度，阿嬤坐起來一點。", "You raise the head of the bed to 60 degrees; Grandma sits up a bit."),
      );
      break;
    case "measure":
      post(
        "bed",
        "narr",
        null,
        tr(
          "鼻尖到耳垂再到劍突：約 55 公分，你用手指捏住那個刻度。",
          "Nose tip to earlobe to xiphoid: about 55 cm. You pinch the tube at that mark.",
        ),
      );
      break;
    case "lube":
      post("bed", "narr", null, tr("前端沾了水溶性潤滑劑。", "You coat the tip with water-soluble lubricant."));
      break;
    case "insert":
      if (!has("sit")) {
        post(
          "bed",
          "alert",
          "afen",
          tr(
            "她躺平你就要插？等一下吐出來會嗆到！先把床頭搖高！",
            "You’re putting it in with her lying flat? She’ll vomit and aspirate! Raise the head of the bed first!",
          ),
        );
        return;
      }
      p.depth = 1;
      post(
        "bed",
        "narr",
        null,
        tr(
          "管子沿著鼻腔底部往後推，阿嬤皺眉，眼淚流出來。到了喉嚨後面，她開始乾嘔。",
          "You slide the tube back along the floor of the nasal cavity. Grandma winces, eyes watering. When it reaches the back of her throat, she starts to gag.",
        ),
      );
      break;
    case "swallow":
      if (p.depth < 1) {
        post("bed", "narr", null, tr("管子還沒放進去。", "The tube isn’t in yet."));
        return;
      }
      p.depth = 2;
      post(
        "bed",
        "narr",
        null,
        tr(
          "「阿嬤，吞口水，吞——」她一吞，管子順著滑下去。",
          "“Grandma, swallow. Swallow—” As she swallows, the tube slides down with it.",
        ),
      );
      break;
    case "advance":
      if (p.depth < 1) {
        post("bed", "narr", null, tr("管子還沒放進去。", "The tube isn’t in yet."));
        return;
      }
      if (p.depth < 2) {
        p.wrong = true;
        post(
          "bed",
          "alert",
          null,
          tr(
            "你直接硬推。阿嬤突然劇烈嗆咳、講不出話，血氧往下掉——管子進了氣管。",
            "You force it straight down. Grandma suddenly chokes and coughs violently, can’t speak, and her SpO₂ drops: the tube is in her trachea.",
          ),
        );
        return;
      }
      p.depth = 3;
      post("bed", "narr", null, tr("推到 55 公分的標記。", "You advance it to the 55 cm mark."));
      break;
    case "confirm":
      if (p.depth < 3) {
        post("bed", "narr", null, tr("還沒推到位。", "It’s not in far enough yet."));
        return;
      }
      post(
        "bed",
        "narr",
        null,
        tr(
          "反抽出暗綠色、帶臭味的液體；打 20 mL 空氣，上腹聽得到咕嚕聲。位置對了。",
          "You aspirate dark green, foul-smelling fluid; a 20 mL air push gives a gurgle over the epigastrium. It’s in the right place.",
        ),
      );
      break;
    case "tape":
      if (!has("confirm")) {
        post(
          "bed",
          "npc",
          "afen",
          tr("位置還沒確認，你就要貼？", "You haven’t confirmed placement and you’re taping it?"),
        );
        return;
      }
      post("bed", "narr", null, tr("膠布貼在鼻樑上固定。", "You tape it to the bridge of her nose."));
      break;
    case "bag":
      if (!has("confirm")) {
        post(
          "bed",
          "alert",
          "afen",
          tr(
            "還沒確認位置欸！在氣管裡接引流你就完了。",
            "You haven’t confirmed placement! If it’s in the trachea and you hook up drainage, you’re done for.",
          ),
        );
        return;
      }
      S.ngAt = S.t;
      S.ngBy = "self";
      S.headUp = true;
      if (S.orders.ng) S.orders.ng.done = true;
      post(
        "bed",
        "result",
        null,
        tr(
          "接上引流袋：馬上流出 400 多 mL 暗綠色、帶臭味的液體。阿嬤長長吐了一口氣：「哇……肚子鬆一點了。」",
          "You connect the bag: over 400 mL of dark green, foul-smelling fluid pours out at once. Grandma lets out a long breath: “Oh… my belly feels looser.”",
        ),
      );
      finding(
        tr("鼻胃管", "NG tube"),
        tr("自己放置，引流出 420 mL 暗綠色帶臭味液體", "Placed by you; 420 mL of dark green, foul-smelling output"),
      );
      closeZoom();
      break;
  }
  if (!has(s)) p.done.push(s);
  spend(s === "insert" || s === "advance" || s === "confirm" ? 1 : 0);
}

function ngPullBack(): void {
  const p = S.ng;
  if (!p) return;
  me("bed", tr("（把管子拉出來）", "(Pulls the tube out)"));
  p.wrong = false;
  p.depth = 0;
  p.done = p.done.filter((x) => x !== "insert" && x !== "swallow" && x !== "advance");
  post(
    "bed",
    "narr",
    null,
    tr(
      "管子拉出來，阿嬤咳了好一陣子才緩過來，血氧慢慢回到 95%。重新來。",
      "The tube comes out. Grandma coughs for a long while before she settles, and her SpO₂ slowly climbs back to 95%. Start over.",
    ),
  );
  spend(1);
}

function startNg(): void {
  if (S.ngAt !== null) {
    note(tr("鼻胃管已經放好了。", "The NG tube is already in."), "bed");
    return;
  }
  if (!atBed()) return;
  S.ng ??= { done: [], depth: 0, wrong: false };
  openZoom({ dlg: "bed", ...ngZoom() });
}

/* ---------- orders ---------- */

function order(id: OrderId): void {
  if (S.orders[id]) return;
  if (id === "ng" && S.ngAt !== null) return;
  if (id === "ct" && S.ngAt === null && !S.orders.ng) {
    post(
      "nurse",
      "npc",
      "afen",
      tr(
        "要推去 CT 喔？她還在一直吐，躺平小心嗆到喔。",
        "Sending her to CT? She’s still vomiting nonstop. Lying flat, watch out she doesn’t aspirate.",
      ),
    );
  }
  const o: Order = { at: S.t, ready: S.t + ORD[id][1], done: false };
  S.orders[id] = o;
  if (id === "ct") S.ctLeave = S.t + 10;
  if (id === "o2") {
    o.ready = S.t;
    o.done = true;
    post("nurse", "npc", "afen", tr("氧氣接上了。", "O2’s on."));
  }
  if (id === "npo") {
    o.done = true;
    post(
      "nurse",
      "npc",
      "afen",
      tr(
        "有跟阿嬤講不能吃喝了。床頭也幫你貼了 NPO。",
        "Told Grandma no food or drink. I put an NPO sign on the bed for you too.",
      ),
    );
  }
}

const PROCEDURE_REPLY: Partial<Record<OrderId, string>> = {
  iv2: tr("第二條 18G 打好了。", "Second line’s in, 18G."),
  fluid: tr("LR 1000 全速。", "LR 1000 running wide open."),
  kcl: tr("KCl 加進去了。", "KCl’s in the bag."),
  abx: tr("Ceftriaxone、Metronidazole 都掛上了。", "Ceftriaxone and metronidazole are both hung."),
  analgesic: tr("Morphine 2 打了。", "Morphine 2 given."),
};

function deliver(id: OrderId): void {
  const o = S.orders[id];
  if (!o) return;
  o.done = true;
  const reply = PROCEDURE_REPLY[id];
  if (reply) {
    if (id === "fluid") S.fluidAt = S.t;
    if (id === "abx") S.abxAt = S.t;
    post("nurse", "npc", "afen", reply);
    return;
  }
  if (id === "ng") {
    if (S.ngAt !== null) return;
    S.ngAt = S.t;
    S.ngBy = "nurse";
    S.headUp = true;
    S.ng = null;
    finding(
      tr("鼻胃管", "NG tube"),
      tr("阿芬放置，引流出 420 mL 暗綠色帶臭味液體", "Placed by Fen; 420 mL of dark green, foul-smelling output"),
    );
    post(
      "nurse",
      "npc",
      "afen",
      tr(
        "NG 放好了，一接上就流了 400 多，暗綠色，有點臭。",
        "NG’s in. Put out over 400 as soon as I hooked it up. Dark green, kind of foul.",
      ),
    );
    return;
  }
  if (id === "foley") {
    finding(tr("導尿", "Foley"), tr("導出 60 mL 深茶色尿", "60 mL of dark, tea-colored urine"));
    post(
      "nurse",
      "npc",
      "afen",
      tr("Foley 放好了，導出來才 60 mL，很濃。", "Foley’s in. Only 60 mL came out, and it’s really concentrated."),
    );
    return;
  }
  const lact = lactate(o.at);
  const results: Partial<Record<OrderId, string>> = {
    lactate: tr(
      `VBG：pH 7.34，HCO₃ 20，Lactate ${lact.toFixed(1)} mmol/L`,
      `VBG: pH 7.34, HCO₃ 20, Lactate ${lact.toFixed(1)} mmol/L`,
    ),
    crp: "CRP 8.6 mg/dL",
    coag: tr("PT 11.4 秒（INR 1.05），aPTT 30 秒", "PT 11.4 s (INR 1.05), aPTT 30 s"),
    ts: tr(
      "血型 B 型 Rh(+)，抗體篩檢陰性，可以備血",
      "Type B Rh(+), antibody screen negative; blood can be crossmatched",
    ),
    ecg: tr("ECG：竇性心搏過速 108 bpm，沒有 ST 變化", "ECG: sinus tachycardia 108 bpm, no ST changes"),
    cxr:
      S.aspAt !== null && o.at >= S.aspAt
        ? tr(
            "CXR：橫膈下沒有游離氣體；右下肺野有新的浸潤（疑吸入）",
            "CXR: no free air under the diaphragm; new right lower lung field infiltrate (suspected aspiration)",
          )
        : tr("CXR：橫膈下沒有游離氣體，肺野清楚", "CXR: no free air under the diaphragm; lungs clear"),
    ct:
      S.taxisAt !== null && S.taxisAt < o.at + 10
        ? tr(
            "腹部 CT：小腸擴張到 4 公分；右側股管內已經沒有腸子，但右下腹一段小腸壁增強變差、周圍積液，有少量游離氣體",
            "CT abd/pelvis: small bowel dilated to 4 cm; no bowel left in the right femoral canal, but a segment of small bowel in the RLQ shows poor wall enhancement with surrounding fluid, and a small amount of free air",
          )
        : tr(
            "腹部 CT：小腸擴張到 4 公分，轉折點在右側股管：一段小腸疝入右側股管（股疝氣），腸壁增強變差、周圍少量積液；沒有游離氣體",
            "CT abd/pelvis: small bowel dilated to 4 cm with the transition point at the right femoral canal: a segment of small bowel herniated into the right femoral canal (femoral hernia), with poor wall enhancement and a little surrounding fluid; no free air",
          ),
  };
  const text = results[id] ?? "";
  S.results.push({ t: S.t, text });
  finding(tr("結果", "Result"), text);
  post("nurse", "result", "afen", text);
}

/* ---------- daughter, CT trips, aspiration, peritonitis ---------- */

function dauArrive(): void {
  S.dauHere = true;
  const m = spawnAt("meihua", "bed", DOOR, [3, 3]);
  m.onArrive = () => {
    m.face = "left";
    rebuildDyn();
  };
  post(
    "bed",
    "npc",
    "meihua",
    tr(
      "媽！我停好車了……醫生，我是她女兒。她是不是吃壞肚子？可以打個點滴就回家嗎？我明天一早要上班……",
      "Mom! I parked the car… Doctor, I’m her daughter. Is it just something she ate? Can she get an IV and go home? I have to be at work first thing tomorrow…",
    ),
  );
  flag("meihua", "!");
}

function aspirate(): void {
  S.aspAt = S.t;
  S.aspMild = S.headUp;
  const where = S.ptLoc === "ct" ? tr("CT 室", "CT room") : tr("15 床", "Bed 15");
  if (S.ptLoc === "ct") {
    S.ptLoc = "bed";
    S.ctBack = null;
    post(
      "bed",
      "narr",
      null,
      tr(
        "CT 室打電話來：阿嬤躺平在檢查台上突然大吐、嗆到，已經推回來了。",
        "CT calls: Grandma suddenly vomited a lot lying flat on the table and aspirated. They’ve wheeled her back.",
      ),
    );
  }
  eventBanner(tr("嘔吐嗆到", "Vomited and aspirated"), where, true);
  post(
    "bed",
    "alert",
    "meihua",
    S.aspMild
      ? tr("醫生！我媽又吐了，一直咳！", "Doctor! My mom threw up again and she can’t stop coughing!")
      : tr(
          "醫生！醫生！我媽吐了一大口，嗆到了，在咳！臉都紫了！",
          "Doctor! Doctor! My mom threw up a huge amount and she’s choking, coughing! Her face is turning purple!",
        ),
  );
  if (!S.dauHere) post("bed", "alert", "afen", tr("15 床吐了、在嗆！", "Bed 15 vomited and she’s choking!"));
  addWait({
    id: "asp",
    text: tr("阿嬤嗆到了！", "Grandma aspirated!"),
    urgent: true,
    icon: tr("！", "!"),
    clearOn: "manual",
  });
  closeZoom();
}

function aspAct(label: string, txt: string): void {
  if (S.aspActs.includes(label)) return;
  S.aspActs.push(label);
  me("bed", tr(`（${label}）`, `(${label})`));
  post("bed", "narr", null, txt);
  if (S.aspActs.length >= 2) clearWait("asp");
  spend(1);
}

function peritonitis(): void {
  S.peritAt = S.t;
  eventBanner(tr("腹膜炎徵象", "Peritoneal signs"), tr("肚子變硬、心跳飆高", "Rigid belly, HR climbing"), true);
  post("bed", "alert", PT, tr("唉唷……痛死了……整個肚子都痛……", "Aiyo… it hurts so much… my whole belly hurts…"));
  const v = vitals();
  S.vitalsSeen.push({ t: S.t, v });
  post(
    "nurse",
    "alert",
    "afen",
    tr(`醫師！15 床 ${vtxt(v)}，肚子硬梆梆的。`, `Doctor! Bed 15 is ${vtxt(v)}, and her belly is rock hard.`),
  );
  if (!S.vsAccepted && !S.rCalled) {
    S.rCalled = true;
    S.rComingAt = S.t + 10;
    post(
      "nurse",
      "npc",
      "afen",
      tr(
        "我直接 call 阿凱了，他說關完傷口就下來。",
        "I called Kai myself. He says he’ll come down as soon as he closes.",
      ),
    );
  }
}

/* ---------- endings ---------- */

function pushOut(): void {
  S.bedAnim = {
    pts: [
      [2, 2],
      [2, 5],
      [11, 5],
      [15, 5],
      [15, 10],
    ],
    t0: performance.now(),
  };
}

function endKind(): EndKind {
  if (S.taxisAt !== null) return "C";
  if (S.peritAt !== null || (S.aspAt !== null && !S.aspMild)) return "B";
  return "A";
}

function finale(by: "gs_vs" | "gs_r"): void {
  const k = endKind();
  const leaveAll = (): void => {
    pushOut();
    leaveBy(ent(by), DOOR);
    if (S.dauHere) leaveBy(ent("meihua"), DOOR);
  };
  const consentLine = S.agreed
    ? {
        who: PT,
        text: tr("醫生，你剛剛講得很清楚。開吧。", "Doctor, you explained it very clearly. Go ahead and operate."),
      }
    : {
        who: by,
        text: tr(
          "阿嬤，這個不開，腸子會破掉。我們一起把它處理好，好不好？",
          "Grandma, if we don’t operate, your bowel will rupture. Let’s take care of this together, okay?",
        ),
      };
  if (k === "A")
    void playEnding(
      "A",
      tr("及時進刀房", "To the OR in Time"),
      [
        {
          who: by,
          text: tr(
            "右側 femoral hernia，strangulated。沒有推，很好。通知開刀房，我刀房見。",
            "Right femoral hernia, strangulated. You didn’t reduce it. Good. Call the OR. I’ll see you there.",
          ),
        },
        consentLine,
        {
          fn: leaveAll,
          text: tr("阿芬拉起床欄，阿嬤被推往手術室。", "Fen raises the bed rails, and Grandma is wheeled to the OR."),
          wait: 3200,
        },
        {
          who: "afen",
          text: tr(
            "學弟，你剛剛拉簾子、叫我陪，阿嬤後來都敢講了。",
            "Hey, you pulled the curtain and called me in to stay with her. That’s why Grandma finally opened up.",
          ),
        },
        {
          text: tr(
            "凌晨一點，手術紀錄：右側股疝氣絞扼，小腸一段約 10 公分發紫，鬆開疝環、熱敷後恢復血色，沒有切腸，修補。",
            "01:00, operative note: strangulated right femoral hernia. About 10 cm of small bowel was dusky; after the hernia ring was released and warm packs applied, its color returned. No resection; hernia repaired.",
          ),
        },
      ],
      () =>
        tr(
          `${S.groinAt !== null ? clock(S.groinAt) : "—"} 找到鼠蹊部團塊，${S.vsAcceptedAt !== null ? clock(S.vsAcceptedAt) : "—"} 報告江主任，在腹膜炎之前進刀房。`,
          `Found the groin lump at ${S.groinAt !== null ? clock(S.groinAt) : "—"}, presented to Dr. Chiang at ${S.vsAcceptedAt !== null ? clock(S.vsAcceptedAt) : "—"}, and she reached the OR before peritonitis set in.`,
        ),
    );
  else if (k === "B")
    void playEnding(
      "B",
      tr("晚了一步", "One Step Too Late"),
      [
        {
          who: by,
          text:
            S.peritAt !== null
              ? tr(
                  "已經 peritonitis 了。推開刀房，抗生素、備血，通知麻醉。",
                  "She’s already peritonitic. Get her to the OR. Antibiotics, crossmatch, call anesthesia.",
                )
              : tr(
                  "嗆過了喔？先顧好呼吸道。開刀房跟麻醉說病人有吸入。",
                  "She aspirated? Secure the airway first. Tell the OR and anesthesia she aspirated.",
                ),
        },
        consentLine,
        {
          fn: leaveAll,
          text: tr(
            "阿嬤被推往手術室。美華跟在床邊一路小跑。",
            "Grandma is wheeled to the OR. Mei-Hua jogs alongside the bed the whole way.",
          ),
          wait: 3200,
        },
        {
          text:
            S.peritAt !== null
              ? tr(
                  "手術紀錄：右側股疝氣絞扼，小腸一段 25 公分壞死，切除吻合。術後轉加護病房。",
                  "Operative note: strangulated right femoral hernia; 25 cm of necrotic small bowel resected with anastomosis. Transferred to the ICU post-op.",
                )
              : tr(
                  "手術紀錄：右側股疝氣絞扼，小腸一段 15 公分壞死，切除吻合。術後吸入性肺炎，轉加護病房觀察。",
                  "Operative note: strangulated right femoral hernia; 15 cm of necrotic small bowel resected with anastomosis. Aspiration pneumonia post-op; transferred to the ICU for monitoring.",
                ),
        },
      ],
      () =>
        tr(
          `腹膜炎：${S.peritAt !== null ? clock(S.peritAt) : "沒有"}；嘔吐嗆到：${S.aspAt !== null ? clock(S.aspAt) : "沒有"}。${by === "gs_r" ? "最後是阿凱下來接手。" : ""}`,
          `Peritonitis: ${S.peritAt !== null ? clock(S.peritAt) : "none"}; aspiration: ${S.aspAt !== null ? clock(S.aspAt) : "none"}.${by === "gs_r" ? " In the end Kai came down and took over." : ""}`,
        ),
    );
  else
    void playEnding(
      "C",
      tr("推回去的腸子", "The Bowel You Pushed Back"),
      [
        {
          who: by,
          text: S.taxisTold
            ? tr(
                "你推回去了？……好，至少你有講。紅成那樣的 hernia 不能推，死掉的腸子推回肚子裡，一樣是死的。",
                "You reduced it? …Fine, at least you told me. A hernia that red is never reduced. Dead bowel pushed back into the belly is still dead.",
              )
            : tr(
                "鼠蹊部是紅的，團塊卻不見了。有人推過嗎？……紅成那樣不能推。",
                "The groin is red, but the lump is gone. Did someone reduce it? …You never push one that red.",
              ),
        },
        { fn: leaveAll, text: tr("阿嬤被推往手術室。", "Grandma is wheeled to the OR."), wait: 3200 },
        {
          text: tr(
            "手術紀錄：剖腹探查。被推回腹腔的一段小腸已經壞死穿孔，腹腔汙染。小腸切除 30 公分，術後轉加護病房。",
            "Operative note: exploratory laparotomy. The segment of small bowel pushed back into the abdomen was necrotic and perforated, with peritoneal contamination. 30 cm of small bowel resected; transferred to the ICU post-op.",
          ),
        },
      ],
      () =>
        tr(
          `${clock(S.taxisAt ?? S.t)} 徒手把絞扼的股疝氣推回去，${S.peritAt !== null ? `${clock(S.peritAt)} 出現腹膜炎` : "之後腸子在腹腔裡壞死"}。${S.taxisTold ? "有誠實告訴主治。" : "沒有告訴主治。"}`,
          `At ${clock(S.taxisAt ?? S.t)} you manually reduced a strangulated femoral hernia; ${S.peritAt !== null ? `peritonitis followed at ${clock(S.peritAt)}` : "the bowel then died inside the abdomen"}. ${S.taxisTold ? "You told the attending honestly." : "You never told the attending."}`,
        ),
    );
}

function endD(): void {
  void playEnding(
    "D",
    tr("腸胃炎", "Gastroenteritis"),
    [
      {
        who: "er",
        text: tr(
          "OK，外科說不是外科問題，那我收內科觀察，打點滴止吐。",
          "OK, surgery says it’s not surgical, so I’ll admit her to medicine for observation. IV fluids and antiemetics.",
        ),
      },
      {
        fn: pushOut,
        text: tr("阿嬤被推上樓到內科病房。", "Grandma is wheeled upstairs to the medicine ward."),
        wait: 3000,
      },
      {
        text: tr(
          "隔天早上 06:40，內科病房：阿嬤血壓 80/40，肚子硬梆梆。急會外科。",
          "06:40 the next morning, medicine ward: Grandma’s BP is 80/40 and her belly is rock hard. Stat surgery consult.",
        ),
      },
      {
        who: "gs_vs",
        text: tr(
          "沒開過刀的老太太 SBO，鼠蹊部有人看過嗎？",
          "An elderly woman with no prior surgery and an SBO. Did anyone examine her groin?",
        ),
      },
    ],
    () =>
      tr(
        `${clock(S.dispoAt ?? S.t)} 回覆照會「不是外科問題」，沒有檢查鼠蹊部。`,
        `At ${clock(S.dispoAt ?? S.t)} you answered the consult “not a surgical problem” without examining the groin.`,
      ),
  );
}

/* ---------- close-ups ---------- */

function bedZoom(): ZoomCfg | null {
  if (!atBed()) return null;
  return {
    title: tr("15 床", "Bed 15"),
    sub: tr("點監視器、臉、肚子、鼠蹊部……", "Tap the monitor, her face, belly, groin…"),
    draw: (c: Ctx, now: number) => {
      const v = vitals();
      const p = perit();
      drawBedScene(c, now, {
        wall: "#dfe8ee",
        wall2: "#d0dbe3",
        base: "#a9b8c4",
        floor: "#c9d6df",
        floor2: "#bac8d2",
        pt: PT,
        belly: false,
        blanket: "#e8e8f0",
        blanket2: "#c9ccd8",
        pale: p !== null ? { s: "#e8d8cc", S: "#cbb8aa" } : S.aspAt !== null ? { s: "#e2d4d0" } : {},
        shiver: S.t > 30 || p !== null,
        iv: true,
        iv2: S.fluidAt !== null ? "#e6f2f8" : S.abxAt !== null ? "#f4e8c8" : undefined,
        mask: S.orders.o2?.done === true,
        companion: S.dauHere ? "meihua" : undefined,
        monitor: (mc, mnow) => {
          drawEcg(mc, 10, 15, 26, 6, v.hr, mnow, "#3be37a");
          ptext(mc, v.hr, 37, 9, "#3be37a");
          ptext(mc, v.spo2, 37, 21, "#4fd6ff");
          if ((v.hr > 118 || v.spo2 < 92) && Math.floor(mnow / 400) % 2) obox(mc, "#ff3b4e", 47, 7, 3, 3);
        },
        extra: (ec) => {
          // Belly distension
          const dz = S.ngAt !== null ? 5 : 7;
          pell(ec, 90, 51, 17, dz, "#1b1426");
          pell(ec, 90, 51, 16, dz - 1, "#e8e8f0");
          R(ec, "#ffffff", 82, 52 - dz + 2, 10, 1);
          if (S.ngAt !== null) {
            pline(ec, 36, 46, 44, 54, "#e8d890", 1);
            pline(ec, 44, 54, 52, 70, "#e8d890", 1);
            obox(ec, "#e8eef0", 46, 70, 12, 14);
            R(ec, "#4a6a2a", 47, 76, 10, 7);
          }
          if (S.orders.foley?.done) {
            obox(ec, "#e8eef0", 150, 72, 10, 12);
            R(ec, "#c89a3a", 151, 80, 8, 3);
          }
          if (S.curtain) {
            R(ec, "#9fc8b8", 182, 0, 10, 70);
            for (let y = 0; y < 70; y += 6) R(ec, "#86b0a0", 184, y, 1, 4);
          }
        },
      });
    },
    hot: () => {
      const hs = bedHots({
        onMonitor: openMon,
        ptLabel: tr("秀琴阿嬤（跟她說話）", "Grandma Hsiu-Chin (talk)"),
        bellyLabel: tr("肚子", "Belly"),
        onBelly: () =>
          setZBar(
            [
              C(tr("看", "Look"), abd.look),
              C(tr("聽腸音", "Auscultate"), abd.listen),
              C(tr("叩診", "Percuss"), abd.percuss),
              C(tr("觸診", "Palpate"), abd.palpate),
              C(tr("肛門指診", "Rectal exam"), rectal),
            ],
            tr("肚子", "Belly"),
          ),
        onPad: tryGroin,
        padLabel: tr("鼠蹊部（大腿根）", "Groin (top of thigh)"),
        onIv: () =>
          note(
            tr(
              `一條 20G（急診打的）${S.orders.iv2?.done ? "＋一條 18G" : ""}${S.fluidAt !== null ? "，LR 全速" : "，N/S 60 mL/h"}${S.abxAt !== null ? "，抗生素" : ""}`,
              `One 20G (placed by the ER)${S.orders.iv2?.done ? " + one 18G" : ""}${S.fluidAt !== null ? ", LR wide open" : ", N/S 60 mL/h"}${S.abxAt !== null ? ", antibiotics" : ""}`,
            ),
            "bed",
          ),
        ...(S.dauHere ? { companion: "meihua" } : {}),
        companionLabel: tr("美華（跟她說話）", "Mei-Hua (talk)"),
      });
      hs.push({ x: 26, y: 38, w: 14, h: 10, label: tr("鼻子（放鼻胃管）", "Nose (place NG tube)"), fn: startNg });
      return hs;
    },
  };
}

function drawGroin(c: Ctx, now: number): void {
  R(c, "#c9d6df", 0, 0, 192, 108);
  // Patient lying supine, viewed from the foot of the bed; patient's right is on the left.
  R(c, "#5f8fc8", 0, 0, 192, 28);
  for (let x = 0; x < 192; x += 12) R(c, "#4f7fb8", x, 0, 1, 28);
  ptext(c, "R", 6, 32, "#3a3f52");
  ptext(c, "L", 182, 32, "#3a3f52");
  const sk = "#e2b48c";
  const skS = "#c8966e";
  obox(c, sk, 20, 26, 152, 44);
  R(c, skS, 20, 26, 152, 3);
  // Umbilicus edge, ASIS, inguinal ligament lines
  pell(c, 96, 30, 3, 2, skS);
  pline(c, 30, 34, 82, 62, "#d3a47c", 1);
  pline(c, 162, 34, 110, 62, "#d3a47c", 1);
  R(c, "#d3a47c", 92, 60, 8, 2);
  // Drape over the pubis
  obox(c, "#5f8fc8", 82, 56, 28, 20);
  R(c, "#4f7fb8", 82, 70, 28, 2);
  // Thighs
  obox(c, sk, 26, 70, 54, 34);
  obox(c, sk, 112, 70, 54, 34);
  R(c, skS, 78, 70, 2, 34);
  R(c, skS, 112, 70, 2, 34);
  // Lower drape over the legs
  R(c, "#5f8fc8", 0, 96, 192, 12);
  // The lump: below the ligament, lateral to the pubic tubercle, on the patient's right.
  const pulse = Math.floor(now / 700) % 2;
  if (S.taxisAt === null) {
    pell(c, 66, 72, 12, 8, "#1b1426");
    pell(c, 66, 72, 11, 7, "#d9826e");
    pell(c, 64, 70, 6, 3, "#ec9e86");
    if (pulse) dither(c, 54, 66, 24, 14, "#c0504a", 0.25);
  } else {
    pell(c, 66, 73, 10, 5, "#dc9a80");
    dither(c, 56, 68, 22, 10, "#c0504a", 0.2);
  }
  // Left groin: normal
  R(c, "#d6a882", 120, 70, 14, 1);
}

function groinZoom(): ZoomCfg {
  const hot: Hotspot[] = [
    {
      x: 50,
      y: 62,
      w: 32,
      h: 20,
      label: tr("右側鼠蹊部的團塊（看、摸）", "Right groin lump (look, feel)"),
      fn: groinRight,
    },
    {
      x: 112,
      y: 62,
      w: 30,
      h: 16,
      label: tr("左側鼠蹊部", "Left groin"),
      fn: () =>
        findOnce(
          tr("摸左側鼠蹊部", "Palpates the left groin"),
          tr("左側鼠蹊部", "Left groin"),
          tr("左側鼠蹊部正常，沒有團塊、沒有壓痛。", "Left groin normal: no mass, no tenderness."),
          1,
        ),
    },
    {
      x: 40,
      y: 28,
      w: 112,
      h: 26,
      label: tr("下腹部", "Lower abdomen"),
      fn: abd.palpate,
    },
  ];
  const bar: Choice[] = [
    C(tr("請她咳嗽", "Ask her to cough"), cough),
    C(tr("試著把它推回去（taxis）", "Try to push it back (taxis)"), taxis, S.taxisAt !== null, "warn"),
    C(tr("蓋好，結束檢查", "Cover her, finish"), () => {
      me("bed", tr("（幫阿嬤蓋好被子）", "(Tucks the blanket back over Grandma)"));
      closeZoom();
    }),
  ];
  return {
    title: tr("鼠蹊部檢查", "Groin Exam"),
    sub: tr("阿芬在旁陪同・簾子拉上", "Fen chaperoning · Curtain drawn"),
    draw: drawGroin,
    hot,
    bar,
    hint: tr("病人的右邊在畫面左邊", "The patient’s right is on the left of the screen"),
  };
}

function drawNg(c: Ctx, now: number): void {
  const p = S.ng;
  R(c, "#dfe8ee", 0, 0, 192, 108);
  R(c, "#c9d6df", 0, 80, 192, 28);
  // Pillow and raised back
  obox(c, "#f4f4f6", 20, S.headUp ? 30 : 60, 70, S.headUp ? 60 : 24);
  const sk = "#e2b48c";
  const skS = "#c8966e";
  const hy = S.headUp ? 22 : 50;
  // Head in profile, facing right
  pell(c, 80, hy + 16, 20, 18, "#1b1426");
  pell(c, 80, hy + 16, 19, 17, sk);
  R(c, "#d8d8de", 62, hy, 20, 12);
  R(c, "#f2f2f6", 64, hy + 2, 12, 4);
  obox(c, sk, 97, hy + 12, 6, 6);
  R(c, skS, 99, hy + 16, 3, 2);
  R(c, "#1b1426", 88, hy + 10, 2, 2);
  R(c, "#b44d5a", 92, hy + 24, 6, 1);
  // Neck & chest
  obox(c, sk, 70, hy + 30, 16, 14);
  obox(c, "#a9c8e8", 50, hy + 42, 70, 30);
  // Airway / oesophagus schematic
  R(c, "#e8c8c0", 82, hy + 30, 3, 14);
  R(c, "#d8b0a8", 78, hy + 30, 3, 14);
  // Tube
  const tube = "#e8d890";
  if (p && p.depth >= 1) {
    pline(c, 102, hy + 16, 90, hy + 22, tube, 1);
    pline(c, 90, hy + 22, 84, hy + 28, tube, 1);
  }
  if (p && p.depth >= 2) pline(c, 84, hy + 28, p.wrong ? 83 : 79, hy + 40, tube, 1);
  if (p?.wrong) {
    pline(c, 83, hy + 28, 84, hy + 44, "#ff5a6a", 1);
    if (Math.floor(now / 250) % 2) ptext(c, "!", 110, hy + 4, "#ff5a6a", 2);
  }
  if (p && p.depth >= 3 && !p.wrong) pline(c, 79, hy + 40, 82, hy + 62, tube, 1);
  // Tube coil in hand at the side
  pell(c, 150, 40, 14, 10, tube);
  pell(c, 150, 40, 10, 7, "#dfe8ee");
  ptext(c, "55", 144, 56, "#3a3f52");
  if (p?.done.includes("lube")) R(c, "#9fd0ef", 160, 46, 4, 2);
}

function ngZoom(): ZoomCfg {
  return {
    title: tr("放鼻胃管", "NG Tube Placement"),
    sub: tr("照順序做；做錯了阿芬會提醒你", "Go in order; Fen will call out mistakes"),
    draw: drawNg,
    bar: () => {
      const p = S.ng;
      if (!p) return [];
      if (p.wrong) return [C(tr("把管子拉出來", "Pull the tube out"), ngPullBack, false, "warn")];
      return NG_ORDER.map((s) => C(NG_STEPS[s], () => ngStep(s), p.done.includes(s)));
    },
    hint: tr(
      "管子在食道還是氣管？咳嗽、講不出話、血氧掉＝不對",
      "Esophagus or trachea? Coughing, can’t speak, SpO₂ dropping = wrong",
    ),
  };
}

/** Plain film (KUB). `up` = erect film with air-fluid levels. */
function drawKub(c: Ctx, up: boolean): void {
  R(c, "#0c0c10", 0, 0, 192, 108);
  R(c, "#2a2a30", 44, 0, 104, 108);
  dither(c, 40, 0, 4, 108, "#2a2a30", 0.5);
  dither(c, 148, 0, 4, 108, "#2a2a30", 0.5);
  // Spine
  for (let y = 2; y < 80; y += 9) obox(c, "#b8b8c0", 90, y, 12, 7, "#9a9aa4");
  // Pelvis
  pell(c, 70, 92, 22, 12, "#b8b8c0");
  pell(c, 70, 92, 18, 9, "#2a2a30");
  pell(c, 122, 92, 22, 12, "#b8b8c0");
  pell(c, 122, 92, 18, 9, "#2a2a30");
  R(c, "#2a2a30", 88, 84, 16, 24);
  // Dilated small-bowel loops, central (gas = dark)
  const loops: [number, number, number][] = [
    [62, 20, 26],
    [104, 22, 28],
    [64, 40, 30],
    [100, 44, 30],
    [72, 60, 26],
    [104, 62, 22],
  ];
  for (const [x, y, w] of loops) {
    obox(c, "#121216", x, y, w, 12, "#5a5a64");
    // valvulae conniventes across the full width
    for (let k = x + 3; k < x + w - 2; k += 4) R(c, "#4a4a54", k, y + 1, 1, 10);
    if (up) {
      R(c, "#3a3a42", x + 1, y + 7, w - 2, 4);
      R(c, "#8a8a94", x + 1, y + 7, w - 2, 1);
    }
  }
  // Little colonic gas
  if (!up) R(c, "#1a1a1e", 140, 30, 6, 4);
  // Tiny gas bubble over the right femoral region (viewer's left)
  pell(c, 52, 98, 3, 2, "#121216");
  ptext(c, "R", 6, 6, "#e8e8ec", 2);
}

function kubZoom(up: boolean): ZoomCfg {
  const mark = (txt: string): void => {
    if (S.kub.has(txt)) {
      note(txt, "pacs");
      return;
    }
    S.kub.add(txt);
    finding("KUB", txt);
    post("pacs", "narr", null, txt);
  };
  return {
    title: "KUB",
    sub: up ? tr("21:55 站立", "21:55 upright") : tr("21:55 平躺", "21:55 supine"),
    draw: (c) => drawKub(c, up),
    hot: [
      {
        x: 60,
        y: 18,
        w: 76,
        h: 58,
        label: tr("中間一圈一圈擴張的腸子", "Dilated loops in the middle"),
        fn: () =>
          mark(
            up
              ? tr(
                  "站立片：多個階梯狀的氣液面（air-fluid levels）",
                  "Upright film: multiple stepladder air-fluid levels",
                )
              : tr(
                  "平躺片：小腸擴張，橫跨整個管徑的皺褶（valvulae conniventes），一層一層像疊錢幣",
                  "Supine film: dilated small bowel with folds crossing the full width of the lumen (valvulae conniventes), stacked like a roll of coins",
                ),
          ),
      },
      {
        x: 132,
        y: 22,
        w: 22,
        h: 16,
        label: tr("大腸的位置", "Where the colon is"),
        fn: () => mark(tr("大腸幾乎沒有氣體", "Almost no gas in the colon")),
      },
      {
        x: 42,
        y: 90,
        w: 18,
        h: 14,
        label: tr("右下角，骨盆外側", "Lower right, lateral to the pelvis"),
        fn: () =>
          mark(
            tr(
              "右側鼠蹊部（股管位置）重疊一小團氣體影",
              "A small gas shadow over the right groin (femoral canal region)",
            ),
          ),
      },
      {
        x: 86,
        y: 0,
        w: 20,
        h: 82,
        label: tr("脊椎", "Spine"),
        fn: () => note(tr("脊椎，沒什麼特別。", "Spine. Nothing special."), "pacs"),
      },
    ],
    photo: up
      ? {
          id: "kub_upright",
          overlay: (c, p) => sideMark(c, p),
          hot: [
            {
              x: 0,
              y: 40,
              w: 144,
              h: 40,
              label: tr("一層一層的氣液面", "Stacked air-fluid levels"),
              fn: () =>
                mark(
                  tr(
                    "站立片：多個階梯狀的氣液面（air-fluid levels）",
                    "Upright film: multiple stepladder air-fluid levels",
                  ),
                ),
            },
            {
              x: 108,
              y: 0,
              w: 36,
              h: 30,
              label: tr("大腸的位置", "Where the colon is"),
              fn: () => mark(tr("大腸幾乎沒有氣體", "Almost no gas in the colon")),
            },
            {
              x: 62,
              y: 0,
              w: 28,
              h: 40,
              label: tr("脊椎", "Spine"),
              fn: () => note(tr("脊椎，沒什麼特別。", "Spine. Nothing special."), "pacs"),
            },
          ],
        }
      : {
          id: "kub_supine",
          overlay: (c, p) => sideMark(c, p),
          hot: [
            {
              x: 12,
              y: 4,
              w: 96,
              h: 62,
              label: tr("一圈一圈擴張的腸子", "Dilated loops"),
              fn: () =>
                mark(
                  tr(
                    "平躺片：小腸擴張，橫跨整個管徑的皺褶（valvulae conniventes），一層一層像疊錢幣",
                    "Supine film: dilated small bowel with folds crossing the full width of the lumen (valvulae conniventes), stacked like a roll of coins",
                  ),
                ),
            },
            {
              x: 0,
              y: 14,
              w: 12,
              h: 52,
              label: tr("大腸的位置", "Where the colon is"),
              fn: () => mark(tr("大腸幾乎沒有氣體", "Almost no gas in the colon")),
            },
            {
              x: 0,
              y: 84,
              w: 30,
              h: 20,
              label: tr("右下角，骨盆外側", "Lower right, lateral to the pelvis"),
              fn: () =>
                mark(
                  tr(
                    "右側鼠蹊部在片子邊緣，看不出明確的東西：KUB 常常照不到股管，看不到不代表沒有疝氣",
                    "The right groin is at the edge of the film and nothing definite shows. A KUB often misses the femoral canal; not seeing a hernia doesn’t mean there isn’t one",
                  ),
                ),
            },
            {
              x: 50,
              y: 0,
              w: 22,
              h: 80,
              label: tr("脊椎", "Spine"),
              fn: () => note(tr("脊椎，沒什麼特別。", "Spine. Nothing special."), "pacs"),
            },
          ],
        },
    bar: [
      C(up ? tr("看平躺片", "Supine film") : tr("看站立片", "Upright film"), () =>
        openZoom({ dlg: "pacs", ...kubZoom(!up) }),
      ),
    ],
    hint: tr("片子上的 R 是病人的右邊", "The R on the film marks the patient’s right"),
  };
}

/** Whether the CT was taken after the hernia had been pushed back. */
const ctAfterTaxis = (): boolean => {
  const o = S.orders.ct;
  return !!o && S.taxisAt !== null && S.taxisAt < o.at + 10;
};

/** Real CT slices (photo mode only; the drawn mode keeps the written report). */
function ctZoom(cor: boolean): ZoomCfg {
  const mark = (txt: string): void => {
    if (S.ctSeen.has(txt)) {
      note(txt, "pacs");
      return;
    }
    S.ctSeen.add(txt);
    finding("CT", txt);
    post("pacs", "narr", null, txt);
  };
  const report = S.results.find((x) => x.text.startsWith(tr("腹部 CT", "CT abd/pelvis")))?.text ?? "";
  return {
    title: tr("腹部 CT", "CT Abd/Pelvis"),
    sub: cor ? tr("冠狀切面", "Coronal") : tr("軸切面・鼠蹊部高度", "Axial · Groin level"),
    draw: photoOnly,
    photo: cor
      ? {
          id: "ct_hernia_cor",
          hot: [
            {
              x: 10,
              y: 4,
              w: 80,
              h: 54,
              label: tr("整片擴張的小腸", "Dilated small bowel throughout"),
              fn: () =>
                mark(
                  tr(
                    "小腸一圈一圈擴張、充滿液體，管徑到 4 公分",
                    "Loop after loop of dilated, fluid-filled small bowel, up to 4 cm across",
                  ),
                ),
            },
            {
              x: 38,
              y: 58,
              w: 26,
              h: 18,
              label: tr("骨盆", "Pelvis"),
              fn: () => note(tr("膀胱和骨盆。", "Bladder and pelvis."), "pacs"),
            },
          ],
        }
      : {
          id: "ct_hernia_ax",
          overlay: (c, p) => sideMark(c, p),
          hot: [
            {
              x: 28,
              y: 24,
              w: 18,
              h: 18,
              label: tr("右鼠蹊部、股靜脈內側的一圈東西", "A loop in the right groin, medial to the femoral vein"),
              fn: () =>
                mark(
                  tr(
                    "右側股管裡有一圈小腸（股疝氣），腸壁顯影變差、周圍有一點積液",
                    "A loop of small bowel in the right femoral canal (femoral hernia), with poor wall enhancement and a little surrounding fluid",
                  ),
                ),
            },
            {
              x: 16,
              y: 38,
              w: 12,
              h: 10,
              label: tr("股動靜脈", "Femoral vessels"),
              fn: () =>
                note(
                  tr(
                    "右側股動靜脈。疝氣就在它的內側。",
                    "Right femoral artery and vein. The hernia sits just medial to them.",
                  ),
                  "pacs",
                ),
            },
            {
              x: 48,
              y: 38,
              w: 30,
              h: 26,
              label: tr("膀胱", "Bladder"),
              fn: () => note(tr("膀胱。", "Bladder."), "pacs"),
            },
            {
              x: 84,
              y: 26,
              w: 18,
              h: 16,
              label: tr("左鼠蹊部", "Left groin"),
              fn: () => note(tr("左側鼠蹊部沒有東西。", "Nothing in the left groin."), "pacs"),
            },
          ],
        },
    bar: [
      ...(ctAfterTaxis()
        ? []
        : [
            C(cor ? tr("看軸切面", "Axial view") : tr("看冠狀切面", "Coronal view"), () =>
              openZoom({ dlg: "pacs", ...ctZoom(!cor) }),
            ),
          ]),
    ],
    hint: report
      ? tr(`報告：${report.replace(/^腹部 CT：/, "")}`, `Report: ${report.replace(/^CT abd\/pelvis: /, "")}`)
      : "",
  };
}

/* ---------- 江主任 & 阿凱 ---------- */

async function vsTalk(text: string, incoming?: unknown): Promise<void> {
  S.vsTalked = true;
  clearWait("vsline");
  const v = vitals();
  const latest =
    incoming === "cb"
      ? tr(
          "（你在開車趕來的路上打給 clerk，問現在生命徵象、NG 引流多少、抗生素和備血。開口直接問，一句話。action 用 continue。）",
          "(You are calling the clerk while driving in. Ask for current vitals, how much the NG has drained, and whether antibiotics and blood are ready. Ask straight out, one sentence. Use action continue.)",
        )
      : undefined;
  await chat(
    text,
    {
      ch: "vs",
      think: "gs_vs",
      tier: "default",
      speakers: { gs_vs: tr("江主任", "Dr. Chiang") },
      body: tr(
        `你在醫學教育遊戲中扮演一般外科主治「江主任」（肝膽胰），${S.trip ? "正在開車趕來醫院" : "晚上在家，今晚是他的 on call"}，接見習醫學生（clerk）的電話。江主任話很少，要 presentation「一句話講重點」，對方講廢話會直接打斷（「重點。」「所以呢？」）。中英夾雜（SBO、hernia、strangulation、lactate、NG decompression）。不直接說出診斷，用問題逼學生。
【真相】78 歲女性，右側股疝氣嵌頓已經絞扼，造成小腸阻塞。沒開過刀的 SBO 一定要檢查疝氣孔。紅、熱、壓痛、推不回去的鼠蹊部團塊＝絞扼，不能徒手推回（taxis），要禁食、NG 減壓、輸液補鉀、抗生素、備血、緊急手術。
【clerk 實際知道的】（講出以外的資訊就追問「哪裡看到的？」）
${known()}
【現況】${clock()}；實際 ${vtxt(v)}；${S.peritAt !== null ? "已經出現腹膜炎徵象。" : ""}${S.taxisAt !== null ? `（clerk 其實在 ${clock(S.taxisAt)} 推過團塊；他沒說就不要提，他說了就嚴厲但簡短地指正。）` : ""}
【action】accept_plan：clerk 說出鼠蹊部（大腿根）紅腫、推不回去的團塊，並提出疝氣嵌頓／絞扼合併小腸阻塞的判斷。你說你現在過去、排急刀，交代一兩件事（NG、抗生素、備血、跟家屬說要開刀）。
need_groin：clerk 報告 SBO 但沒有提到檢查過鼠蹊部或疝氣孔：反問「沒開過刀的老太太 SBO，hernia orifice 看了沒？」後掛電話。
hang_up：clerk 講了一分鐘還講不出重點、或只說腸胃炎。continue：其他。${S.trip ? "你已經在路上，一律用 continue。" : ""}
【told_taxis】clerk 這次有親口說他把團塊推回去了就填 true，否則 false。
【urgency】只在 accept_plan 時填：依 clerk 在電話裡傳達出來的急迫與清楚程度（不是真實狀況）。high：講出絞扼徵象（紅、熱、推不回去）或 lactate、心跳等具體數字並請你立刻來；medium：有重點但不夠具體；low：含糊。`,
        `In a medical education game you play "Dr. Chiang," a general surgery attending (HPB), ${S.trip ? "currently driving in to the hospital" : "at home for the evening; he is on call tonight"}, taking a phone call from a clerk (medical student on clerkship). Dr. Chiang says very little. He wants a presentation that gets to the point in one sentence, and cuts off any rambling ("The point." "So?"). He talks in clinical shorthand (SBO, hernia, strangulation, lactate, NG decompression). He never states the diagnosis outright; he pushes the student with questions.
[TRUTH] 78-year-old woman with an incarcerated right femoral hernia that has already strangulated, causing a small bowel obstruction. An SBO in a patient who has never had surgery always requires checking the hernia orifices. A groin lump that is red, hot, tender and irreducible = strangulation: it must not be manually reduced (taxis). She needs NPO, NG decompression, IV fluids with potassium replacement, antibiotics, blood crossmatched, and emergency surgery.
[WHAT THE CLERK ACTUALLY KNOWS] (if the clerk states anything beyond this, press them: "Where did you see that?")
${known()}
[CURRENT STATE] ${clock()}; actual ${vtxt(v)}; ${S.peritAt !== null ? "Peritoneal signs have already appeared. " : ""}${S.taxisAt !== null ? `(The clerk actually pushed the lump back in at ${clock(S.taxisAt)}. If he doesn't mention it, don't bring it up; if he does, correct him sternly but briefly.)` : ""}
[action] accept_plan: the clerk reports a red, swollen, irreducible lump in the groin (top of the thigh) and gives an assessment of an incarcerated/strangulated hernia with small bowel obstruction. You say you're coming in now and booking an emergency case, and give one or two instructions (NG, antibiotics, crossmatch, tell the family she needs surgery).
need_groin: the clerk presents an SBO but does not mention examining the groin or hernia orifices: ask back "Elderly woman, never had surgery, SBO. Did you check the hernia orifices?" and then hang up.
hang_up: the clerk has talked for a minute without getting to the point, or only says gastroenteritis. continue: anything else.${S.trip ? " You are already on the way: always use continue." : ""}
[told_taxis] true if the clerk explicitly said in this message that he pushed the lump back in; otherwise false.
[urgency] fill only with accept_plan: based on how urgent and clear the clerk sounded on the phone (not the actual situation). high: names signs of strangulation (red, hot, irreducible) or concrete numbers like lactate or heart rate, and asks you to come right away; medium: gets to the point but not specific enough; low: vague.`,
      ),
      extra: `"action":"continue","urgency":"medium","told_taxis":false`,
      after(r) {
        if (r.told_taxis === true && S.taxisAt !== null) S.taxisTold = true;
        if (r.action === "hang_up") post("vs", "narr", null, tr("江主任掛了電話。", "Dr. Chiang hangs up."));
        if (r.action === "need_groin") {
          S.vsHinted = true;
          post("vs", "narr", null, tr("江主任掛了電話。", "Dr. Chiang hangs up."));
        }
        if (r.action === "accept_plan" && !S.vsAccepted) {
          if (S.groinAt === null) {
            post(
              "vs",
              "npc",
              "gs_vs",
              tr(
                "你有看到嗎？自己看過鼠蹊部再打給我。",
                "Did you see it yourself? Examine the groin yourself, then call me back.",
              ),
            );
            S.vsHinted = true;
            return;
          }
          const urg: TripUrgency = isTripUrgency(r.urgency) ? r.urgency : "medium";
          const eta = { high: 8, medium: 14, low: 22 }[urg];
          S.vsAccepted = true;
          S.vsAcceptedAt = S.t;
          S.vsArriveAt = S.t + eta;
          S.vsCbAt = S.t + Math.round(eta / 2);
          S.rComingAt = null;
          startVsTrip(urg, eta);
          eventBanner(tr("排急刀", "Emergency case booked"), tr("江主任在路上", "Dr. Chiang is on the way"));
          post(
            "vs",
            "narr",
            null,
            tr(
              "江主任掛了電話。阿芬那邊的電話馬上響了：開刀房在問 15 床。",
              "Dr. Chiang hangs up. Fen’s phone rings right away: the OR is asking about Bed 15.",
            ),
          );
        }
      },
      cost: incoming ? 0 : 2,
    },
    latest,
  );
}

/* ---------- dialogs ---------- */

function nurseChoices(): ChoiceItem[] {
  const o = S.orders;
  const btn = (id: OrderId) =>
    C(
      ORD[id][0],
      () => {
        me("nurse", tr(`（${ORD[id][0]}）`, `(${ORD[id][0]})`));
        order(id);
        spend(1);
      },
      !!o[id] || (id === "ng" && S.ngAt !== null),
    );
  return [
    tr("檢驗", "Labs"),
    ...(["lactate", "crp", "coag", "ts", "ecg"] as const).map(btn),
    tr("影像", "Imaging"),
    ...(["cxr", "ct"] as const).map(btn),
    tr("處置", "Treatment"),
    ...(["npo", "ng", "iv2", "fluid", "kcl", "foley", "abx", "analgesic", "o2"] as const).map(btn),
    "sep",
    C(tr("請阿芬來陪同檢查", "Ask Fen to chaperone"), askChaperone, S.chap || S.chapAsked),
  ];
}

/** Airway actions after aspiration; the label is also the key in `S.aspActs`. */
const ASP_SIT = tr("扶她坐起、頭偏一側", "Sit her up, turn her head");
const ASP_SUCTION = tr("抽吸口腔", "Suction the mouth");
const ASP_O2 = tr("給氧氣", "Give O2");

function bedChoices(): ChoiceItem[] {
  if (!atBed()) return [tr("阿嬤在 CT 室", "Grandma is at CT")];
  const items: ChoiceItem[] = [];
  if (S.aspAt !== null && S.aspActs.length < 3 && S.t - S.aspAt < 15)
    items.push(
      tr("嗆到了！", "Aspiration!"),
      C(
        ASP_SIT,
        () => {
          S.headUp = true;
          aspAct(
            ASP_SIT,
            tr(
              "你把床頭搖高，扶阿嬤側過頭，讓嘔吐物流出來。",
              "You raise the head of the bed and turn Grandma’s head to the side so the vomit can drain out.",
            ),
          );
        },
        S.aspActs.includes(ASP_SIT),
      ),
      C(
        ASP_SUCTION,
        () =>
          aspAct(
            ASP_SUCTION,
            tr(
              "你拿牆上的抽吸管把她嘴巴裡的嘔吐物抽乾淨。",
              "You grab the wall suction and clear the vomit from her mouth.",
            ),
          ),
        S.aspActs.includes(ASP_SUCTION),
      ),
      C(
        ASP_O2,
        () => {
          order("o2");
          aspAct(ASP_O2, tr("鼻導管接上，3 L/min。", "Nasal cannula on, 3 L/min."));
        },
        S.aspActs.includes(ASP_O2),
      ),
      "sep",
    );
  items.push(
    C(tr("床邊特寫", "Bedside close-up"), () => {
      const z = bedZoom();
      if (z) openZoom({ dlg: "bed", ...z });
    }),
    C(tr("看監視器", "Check monitor"), openMon),
    C(S.curtain ? tr("拉開簾子", "Open curtain") : tr("拉上簾子", "Draw curtain"), toggleCurtain),
    C(
      tr("床頭搖高", "Raise head of bed"),
      () => {
        S.headUp = true;
        me("bed", tr("（把床頭搖高）", "(Raises the head of the bed)"));
        post("bed", "narr", null, tr("床頭搖到 45 度。", "Head of bed up to 45 degrees."));
      },
      S.headUp,
    ),
    tr("肚子", "Belly"),
    C(tr("看", "Look"), abd.look),
    C(tr("聽腸音", "Auscultate"), abd.listen),
    C(tr("叩診", "Percuss"), abd.percuss),
    C(tr("觸診", "Palpate"), abd.palpate),
    tr("鼠蹊部", "Groin"),
    C(tr("解釋並徵求同意", "Explain, ask consent"), explainExam, S.consent),
    C(tr("請阿芬陪同", "Ask Fen to chaperone"), askChaperone, S.chap || S.chapAsked),
    C(tr("檢查鼠蹊部", "Examine groin"), tryGroin),
    C(tr("肛門指診", "Rectal exam"), rectal, S.rectal),
    tr("處置", "Treatment"),
    C(tr("自己放鼻胃管", "Place NG tube yourself"), startNg, S.ngAt !== null),
  );
  return items;
}

const dialogs: Level["dialogs"] = {
  bed: {
    title: tr("15 床", "Bed 15"),
    sub: () =>
      S.dauHere
        ? tr("秀琴阿嬤和女兒美華", "Grandma Hsiu-Chin and her daughter Mei-Hua")
        : tr("秀琴阿嬤，78 歲", "Grandma Hsiu-Chin, 78"),
    pics: () => (S.dauHere ? [PT, "meihua"] : [PT]),
    ch: "bed",
    talk: atBed,
    ph: tr("對阿嬤說…", "Say to Grandma…"),
    choices: bedChoices,
    async onTalk(text) {
      const sp: Record<string, string> = { [PT]: tr("秀琴阿嬤", "Grandma Hsiu-Chin") };
      if (S.dauHere) sp.meihua = tr("女兒美華", "Mei-Hua (daughter)");
      await chat(text, {
        ch: "bed",
        think: PT,
        speakers: sp,
        body: tr(
          `你在醫學教育遊戲中扮演急診 15 床的病人「秀琴阿嬤」${S.dauHere ? "和她的女兒「美華」" : ""}。玩家是一般外科的見習醫學生（clerk），被急診照會來看她。
秀琴阿嬤：78 歲，客家庄長大，講話夾台語（「歹勢」「艱苦」「唉唷」），很客氣、很能忍、怕麻煩別人，認定自己是「吃到不乾淨的粽子」。
美華：48 歲，會計，女兒，急性子，一直想知道能不能打完點滴回家，明天要上班。媽媽叫她不要提那顆，但醫療人員直接問到、或說明這很重要時她會說，而且說得比媽媽具體。
【隱藏真相，不能說出】右側股疝氣嵌頓已經絞扼，造成小腸阻塞。
【可揭露的事實】被問到才說，括號是 id：
${Object.entries(FACTS)
  .map(([k, v]) => `- (${k}) ${v}`)
  .join("\n")}
特別規則：
- lump、stuck 只有 clerk 具體問到「鼠蹊部／大腿根／下面有沒有腫一塊／有沒有脫腸、疝氣」才說。籠統問「哪裡痛」她只會指肚子。${S.curtain ? "" : "簾子沒拉、外面很多人時，阿嬤會壓低聲音含糊帶過（「沒有啦……」），不會講 lump。"}
- 阿嬤講到那顆會很不好意思；clerk 態度尊重、說明保密或拉上簾子時才肯講清楚。
- hid、worry：阿嬤不會主動說；美華在場被問到「還有什麼沒講的」時會說 hid。worry 在提到開刀時阿嬤會說。
- surgery 很重要：問到「以前有沒有開過刀」就照實說沒有。
【行動】act 只能是 none、consent_exam、agree_surgery、explained。
- consent_exam：clerk 跟阿嬤解釋要檢查鼠蹊部／大腿根並徵求同意，阿嬤同意了。${S.curtain ? "" : "簾子沒拉時阿嬤會說外面人多，不要給 consent_exam。"}
- agree_surgery：clerk 用阿嬤聽得懂的話說明為什麼需要開刀、不開的風險，阿嬤（在美華勸說下）同意開刀。
- explained：clerk 跟美華說清楚可能是疝氣卡住、腸子缺血、需要緊急手術、可能要切一段腸子，美華理解了（不再要求回家）。
【狀態】${clock()}；簾子${S.curtain ? "拉上了" : "沒拉"}；${S.chap ? "護理師阿芬在床邊陪同；" : ""}${S.ngAt !== null ? "已經插了鼻胃管，阿嬤講話有點沙啞但肚子比較不脹；" : ""}${S.taxisAt !== null ? "團塊剛被推回去；" : ""}阿嬤：${condition()}。已揭露：${[...S.revealed].join(",") || "無"}`,
          `In a medical education game you play the patient in ER Bed 15, "Grandma Hsiu-Chin"${S.dauHere ? ', and her daughter "Mei-Hua"' : ""}. The player is a general surgery clerk (medical student on clerkship) whom the ER has consulted to see her.
Grandma Hsiu-Chin: 78, grew up in a Hakka village. She speaks plain, folksy English, full of little apologies and sighs ("sorry, sorry to trouble you", "it's been so hard", "aiyo"). Very polite, very stoic, afraid of troubling anyone. She is convinced she just "ate a bad rice dumpling."
Mei-Hua: 48, an accountant, the daughter. Impatient; keeps wanting to know whether her mother can finish the IV and go home, because she has work tomorrow. Her mother told her not to mention the lump, but if the medical staff ask directly, or explain that it matters, she will tell, and in more concrete detail than her mother.
[HIDDEN TRUTH, NEVER SAY IT] An incarcerated right femoral hernia that has already strangulated, causing a small bowel obstruction.
[FACTS THAT CAN BE REVEALED] Only when asked; the id is in parentheses:
${Object.entries(FACTS)
  .map(([k, v]) => `- (${k}) ${v}`)
  .join("\n")}
Special rules:
- lump and stuck are revealed only when the clerk specifically asks about the groin / top of the thigh / any swelling down there / a rupture or hernia. If asked vaguely "where does it hurt," she only points to her belly.${S.curtain ? "" : ' With the curtain open and lots of people around, Grandma lowers her voice and brushes it off ("oh, it\'s nothing…") and will not mention the lump.'}
- Grandma is very embarrassed to talk about the lump; she will only explain it clearly if the clerk is respectful, mentions confidentiality, or draws the curtain.
- hid, worry: Grandma never volunteers these. If Mei-Hua is present and is asked "is there anything else you haven't told us," she reveals hid. Grandma says worry when surgery comes up.
- surgery matters: if asked "have you ever had an operation," answer truthfully that she hasn't.
[ACTIONS] act must be one of none, consent_exam, agree_surgery, explained.
- consent_exam: the clerk explains to Grandma that they need to examine her groin / top of the thigh and asks her permission, and Grandma agrees.${S.curtain ? "" : " With the curtain open, Grandma says there are too many people around; do not give consent_exam."}
- agree_surgery: the clerk explains in words Grandma can understand why she needs surgery and the risks of not operating, and Grandma (with Mei-Hua's urging) agrees to surgery.
- explained: the clerk clearly tells Mei-Hua it is probably a stuck hernia, the bowel is losing its blood supply, she needs emergency surgery, and part of the bowel may have to be removed, and Mei-Hua understands (and stops asking to go home).
[STATE] ${clock()}; curtain ${S.curtain ? "drawn" : "open"}; ${S.chap ? "Nurse Fen is at the bedside as chaperone; " : ""}${S.ngAt !== null ? "an NG tube is in; Grandma's voice is a little hoarse but her belly is less bloated; " : ""}${S.taxisAt !== null ? "the lump was just pushed back in; " : ""}Grandma: ${condition()}. Already revealed: ${[...S.revealed].join(",") || "none"}`,
        ),
        extra: `"revealed":[],"act":"none"`,
        after(r) {
          for (const id of strList(r.revealed)) if (isFact(id)) S.revealed.add(id);
          if (r.act === "consent_exam" && S.curtain) S.consent = true;
          if (r.act === "agree_surgery") S.agreed = true;
          if (r.act === "explained" && S.dauHere) S.explained = true;
        },
      });
    },
  },
  nurse: {
    title: tr("阿芬", "Fen"),
    sub: tr("急診護理師", "ER nurse"),
    pics: ["afen"],
    ch: "nurse",
    talk: true,
    ph: tr("對阿芬說，也可以口頭開醫囑", "Talk to Fen, or give verbal orders"),
    choices: nurseChoices,
    async onTalk(text) {
      const last = S.vitalsSeen.at(-1);
      await chat(text, {
        ch: "nurse",
        think: "afen",
        speakers: { afen: tr("阿芬", "Fen") },
        cost: 1,
        body: tr(
          `你在醫學教育遊戲中扮演急診資深護理師「阿芬」，十二年資歷，很忙、講話直接、偶爾吐槽但心腸好，台式中英夾雜。照醫師口頭醫囑做事，不替 clerk 做診斷；不妥的醫囑會說「醫師你確定嗎？」。上次（婦產科那個月）她也帶過這位 clerk。
【隱藏真相，不能說出】右側股疝氣絞扼合併小腸阻塞。
【現況】${clock()}；病人${atBed() ? "在 15 床" : "在 CT 室"}；最近的生命徵象：${last ? vtxt(last.v) : "分診 BP 138/80，HR 102，37.6°C"}
clerk 目前知道與做過的：
${known()}
【可開立的醫囑 id】${Object.entries(ORD)
            .map(([k, v]) => `${k} ${v[0]}`)
            .join("；")}
【行動】clerk 明確要求的醫囑，把 id 放進 orders。clerk 請你陪同做鼠蹊部或私密部位檢查時 chaperone 填 true。被問到結果只能說已經出來的。`,
          `In a medical education game you play "Fen," a senior ER nurse with twelve years of experience. Very busy, blunt, the occasional wisecrack, but kind at heart; she talks in brisk ER shorthand. She carries out the doctors' verbal orders and does not make the diagnosis for the clerk; for an inappropriate order she says "Doctor, are you sure?" She also worked with this clerk last time (during the OB/GYN month).
[HIDDEN TRUTH, NEVER SAY IT] Strangulated right femoral hernia with small bowel obstruction.
[CURRENT STATE] ${clock()}; patient ${atBed() ? "in Bed 15" : "at CT"}; most recent vitals: ${last ? vtxt(last.v) : "triage BP 138/80, HR 102, 37.6°C"}
What the clerk currently knows and has done:
${known()}
[ORDER IDS AVAILABLE] ${Object.entries(ORD)
            .map(([k, v]) => `${k} ${v[0]}`)
            .join("; ")}
[ACTIONS] Put the id of every order the clerk explicitly asks for into orders. If the clerk asks you to chaperone a groin or intimate exam, set chaperone to true. When asked about results, only report ones that are already back.`,
        ),
        extra: `"orders":[],"chaperone":false`,
        after(r) {
          for (const id of strList(r.orders)) if (isOrder(id)) order(id);
          if (r.chaperone === true) askChaperone();
        },
      });
    },
  },
  er: {
    title: tr("阿哲", "Che"),
    sub: tr("急診住院醫師", "ER resident"),
    pics: ["er"],
    ch: "er",
    talk: true,
    ph: tr("對阿哲說…", "Say to Che…"),
    async onTalk(text) {
      await chat(text, {
        ch: "er",
        think: "er",
        speakers: { er: tr("阿哲", "Che") },
        cost: 1,
        body: tr(
          `你在醫學教育遊戲中扮演急診第二年住院醫師「阿哲」，手上十幾床，講話快、想趕快清床。上個月他照會婦產科時被打臉過（那個「盲腸炎」其實是子宮外孕），所以有點不服氣也有點心虛。他照會一般外科看 15 床秀琴阿嬤（78 歲，吐了一整天、肚子脹），他覺得是腸胃炎合併脫水，「KUB 有點怪，麻煩外科看一下」。他沒看鼠蹊部。
如果 clerk 提到鼠蹊部有團塊或疝氣卡住，他會說「推回去就好啦，我以前在急診推過一堆，推回去就可以收內科了」（這是錯的建議，但他講得很自然）。
【行動】act 只能是 none 或 dispo_medicine。只有 clerk 明確回覆照會「不是外科問題／可以收內科／可以回家」時用 dispo_medicine；其他都 none。
不要說出診斷。
【clerk 目前知道的】
${known()}`,
          `In a medical education game you play "Che," a second-year emergency medicine resident with a dozen-plus beds, who talks fast and wants to clear beds quickly. Last month, when he consulted OB/GYN, he was proven wrong (that "appendicitis" was actually an ectopic pregnancy), so he's a bit defensive and a bit unsure of himself. He has consulted general surgery to see Bed 15, Grandma Hsiu-Chin (78, vomiting all day, distended belly). He thinks it's gastroenteritis with dehydration: "The KUB looks a bit off, could surgery take a look." He did not examine the groin.
If the clerk mentions a groin lump or a stuck hernia, he says "Just push it back in. I've reduced tons of those in the ER. Push it back and we can admit her to medicine." (This is wrong advice, but he says it very naturally.)
[ACTIONS] act must be none or dispo_medicine. Use dispo_medicine only when the clerk explicitly answers the consult with "not a surgical problem / OK to admit to medicine / OK to go home"; otherwise none.
Do not state the diagnosis.
[WHAT THE CLERK CURRENTLY KNOWS]
${known()}`,
        ),
        extra: `"act":"none"`,
        after(r) {
          if (r.act === "dispo_medicine" && S.dispoAt === null) {
            if (S.groinAt !== null || S.vsAccepted) {
              post(
                "er",
                "npc",
                "er",
                tr(
                  "等等，你剛不是說鼠蹊部有東西？主任怎麼說？",
                  "Wait, didn’t you just say there was something in the groin? What did the attending say?",
                ),
              );
              return;
            }
            S.dispoAt = S.t;
            S.endAt = S.t + 1;
          }
        },
      });
    },
  },
  vs: {
    title: tr("江主任", "Dr. Chiang"),
    sub: () =>
      S.vsHere
        ? tr("到場了", "Here now")
        : S.trip
          ? tr("趕來的路上", "On the way in")
          : tr("電話（在家）", "Phone (at home)"),
    pics: ["gs_vs"],
    ch: "vs",
    talk: true,
    ph: tr("向江主任報告（一句話講重點）…", "Present to Dr. Chiang (one sentence, the point)…"),
    keepOpen: true,
    onTalk: (text) => vsTalk(text),
    onCall: (reason) => vsTalk("", reason),
  },
  r: {
    title: tr("阿凱（R3）", "Kai (R3)"),
    sub: () =>
      S.rHere
        ? tr("從刀房下來了", "Down from the OR")
        : tr("在刀房開 appendectomy，流動護理師拿著電話", "Mid-appendectomy in the OR; the circulator holds the phone"),
    pics: ["gs_r"],
    ch: "r",
    talk: true,
    ph: tr("跟阿凱說（他手是濕的，講快一點）…", "Talk to Kai (he’s scrubbed in, be quick)…"),
    keepOpen: true,
    async onTalk(text) {
      await chat(text, {
        ch: "r",
        think: "gs_r",
        speakers: { gs_r: tr("阿凱", "Kai") },
        cost: 1,
        body: tr(
          `你在醫學教育遊戲中扮演外科第三年住院醫師「阿凱」，正在開刀房開一台 appendectomy，流動護理師把電話拿到他耳邊。很累、講話很短（每次一兩句）。人不壞，會給一個方向，但不會替 clerk 做完：
- 如果 clerk 講 SBO：他會問「以前開過刀嗎？hernia 看了嗎？」
- 如果 clerk 已經找到鼠蹊部紅腫推不回去的團塊：「那是 strangulation，不要推，直接打給主任。」
- 他這台還要二、三十分鐘。叫 clerk 自己打給江主任。
不要說出完整診斷。
【clerk 目前知道的】
${known()}`,
          `In a medical education game you play "Kai," a third-year surgery resident in the middle of an appendectomy in the OR; the circulating nurse is holding the phone to his ear. Exhausted, speaks very briefly (one or two sentences each time). Not a bad guy; he'll point the clerk in a direction but won't do the work for them:
- If the clerk says SBO: he asks "Any prior surgery? Did you check for a hernia?"
- If the clerk has already found a red, swollen, irreducible groin lump: "That's strangulation. Don't push it back. Call the attending directly."
- His case will take another twenty or thirty minutes. Tell the clerk to call Dr. Chiang themselves.
Do not state the full diagnosis.
[WHAT THE CLERK CURRENTLY KNOWS]
${known()}`,
        ),
      });
    },
  },
  phone: {
    title: tr("護理站電話", "Nursing Station Phone"),
    sub: tr("要打給誰？", "Who do you call?"),
    ch: "phone",
    choices() {
      return [
        C(tr("打給江主任（一般外科主治）", "Call Dr. Chiang (GS attending)"), () => openDialog("vs"), S.vsHere),
        C(tr("打給阿凱（刀房）", "Call Kai (OR)"), () => {
          me("phone", tr("（打到 5 房找阿凱）", "(Calls OR 5 for Kai)"));
          openDialog("r");
          spend(1);
        }),
        C(tr("打給開刀房排刀", "Call the OR to book"), () => {
          me("phone", tr("（打給開刀房）", "(Calls the OR)"));
          post(
            "phone",
            "npc",
            null,
            S.vsAccepted
              ? tr(
                  "開刀房：江主任剛剛打來了，15 床排在 5 房下一台。",
                  "OR: Dr. Chiang just called. Bed 15 is next up in Room 5.",
                )
              : tr(
                  "開刀房：主治同意了嗎？請主治或總醫師打來排。",
                  "OR: Has the attending approved it? The attending or the chief resident needs to call to book.",
                ),
          );
          spend(1);
        }),
      ];
    },
  },
  lab: {
    title: tr("急診電腦", "ER Computer"),
    sub: tr("檢驗結果", "Results"),
    ch: "lab",
    doc: doc(() => {
      const r = S.results.map((x): [string, string] => [clock(x.t), x.text]);
      const p = (Object.entries(S.orders) as [OrderId, Order][])
        .filter(([, o]) => !o.done)
        .map(([id, o]): [string, string] => [ORD[id][0], tr(`約 ${clock(o.ready)}`, `~${clock(o.ready)}`)]);
      return [
        [tr("急診已驗", "ER labs"), INIT_LABS],
        [
          tr("分診", "Triage"),
          tr("22:05　BP 138/80，HR 102，RR 20，37.6°C，SpO₂ 97%", "22:05 · BP 138/80, HR 102, RR 20, 37.6°C, SpO₂ 97%"),
        ],
        ...r,
        ...p,
      ];
    }),
  },
  pacs: {
    title: "PACS",
    sub: tr("15 床的影像", "Bed 15 imaging"),
    ch: "pacs",
    choices() {
      return [
        C(tr("KUB（平躺）", "KUB (supine)"), () => openZoom({ dlg: "pacs", ...kubZoom(false) })),
        C(tr("KUB（站立）", "KUB (upright)"), () => openZoom({ dlg: "pacs", ...kubZoom(true) })),
        C(tr("腹部 CT", "CT Abd/Pelvis"), () => {
          const ct = S.orders.ct;
          if (ct?.done && getImagery() === "photo") {
            openZoom({ dlg: "pacs", ...ctZoom(ctAfterTaxis()) });
            return;
          }
          note(
            ct
              ? ct.done
                ? (S.results.find((x) => x.text.startsWith(tr("腹部 CT", "CT abd/pelvis")))?.text ?? "")
                : tr(`還在排，約 ${clock(ct.ready)} 出報告。`, `Still in the queue; report around ${clock(ct.ready)}.`)
              : tr("沒有排 CT。", "No CT ordered."),
            "pacs",
          );
        }),
      ];
    },
  },
  board: {
    title: tr("急診白板", "ER Whiteboard"),
    sub: tr("觀察區", "Observation area"),
    ch: "board",
    doc: doc(() =>
      tr(
        [
          ["15 床", "陳秀琴 78F　N/V、腹脹　AGE? dehydration?　照會 GS"],
          ["16 床", "張先生 26M　N/V/D　生蠔（同行 3 人都拉）"],
          ["17 床", "空床"],
          ["今晚 GS on call", "R：阿凱（刀房 5 房）　VS：江主任"],
        ],
        [
          ["Bed 15", "Chen Hsiu-Chin 78F · N/V, distension · AGE? dehydration? · GS consult"],
          ["Bed 16", "Mr. Chang 26M · N/V/D · oysters (all 3 in his party sick)"],
          ["Bed 17", "Empty"],
          ["GS on call tonight", "R: Kai (OR 5) · VS: Dr. Chiang"],
        ],
      ),
    ),
  },
};

/* ---------- level ---------- */

export const GS2: Level = {
  id: "gs-er",
  no: tr("二", "2"),
  short: tr("第二關　急診", "Level 2 · ER"),
  title: tr("急診照會：腸胃炎？", "ER Consult: Gastroenteritis?"),
  place: tr("急診觀察區", "ER observation area"),
  blurb: tr(
    "78 歲阿嬤吐了一整天，急診說是腸胃炎。",
    "A 78-year-old grandma has vomited all day. The ER says it’s gastroenteritis.",
  ),
  cast: [PT, "meihua", "afen", "er", "gs_vs", "gs_r"],
  intro: tr(
    [
      "週二晚上 22:30，你是今晚跟值班的一般外科 clerk。阿凱學長在刀房開一台盲腸炎，叫你先去急診看照會。",
      "照會單寫著：15 床，78 歲女性，嘔吐一整天、腹脹，「AGE？脫水？KUB 有點怪，請外科評估」。阿哲（急診 R）在護理站對你招手。",
    ],
    [
      "Tuesday, 22:30. You’re the general surgery clerk on call tonight. Kai, your senior resident, is in the OR doing an appendectomy and has sent you down to the ER to see a consult.",
      "The consult reads: Bed 15, 78-year-old woman, vomiting all day, abdominal distension. “AGE? Dehydration? KUB looks a bit off, please evaluate.” Che, the ER resident, waves you over from the nurses’ station.",
    ],
  ),
  start: 22 * 60 + 30,
  maxT: 90,
  night: true,
  idleSec: 12,
  map: [
    "####################",
    "#cccccaaaaa#bbbbbbb#",
    "#cccccaaaaa#bbbbbbb#",
    "#cccccaaaaa#bbbbbbb#",
    "#cccccaaaaa#bbbbbbb#",
    "#aaaaaaaaaaabbbbbbb#",
    "#aaaaaaaaaaabbbbbbb#",
    "#dddddaaaaa#bbbbbbb#",
    "#dddddaaaaa#bbbbbbb#",
    "#dddddaaaaa#bbbbbbb#",
    "#dddddaaaaa#bbbbbbb#",
    "####################",
  ],
  zones: {
    a: ["#dfe8ee", "#c9d6df", "#d4dfe7"],
    b: ["#e9e1cf", "#d8cdb5", "#e0d6c0"],
    c: ["#e2ece6", "#cddcd2", "#d8e5dc"],
    d: ["#e2e6ee", "#cfd4e0", "#d8dce6"],
  },
  windows: [],
  exits: [DOOR],
  chName: {
    bed: tr("15 床", "Bed 15"),
    nurse: tr("阿芬", "Fen"),
    er: tr("阿哲", "Che"),
    vs: tr("江主任", "Dr. Chiang"),
    r: tr("阿凱", "Kai"),
    phone: tr("電話", "Phone"),
    lab: tr("急診電腦", "ER computer"),
    pacs: "PACS",
    board: tr("白板", "Whiteboard"),
    sys: tr("急診", "ER"),
  },
  objects: [
    { id: "suction", type: "suction", x: 1, y: 1 },
    { id: "mon", type: "monitorStand", x: 1, y: 2, label: tr("監視器", "Monitor") },
    { id: "bed", type: "gsbed", x: 2, y: 2, h: 2, label: tr("15 床：秀琴阿嬤", "Bed 15: Grandma Hsiu-Chin") },
    { id: "curtain", type: "rail", x: 5, y: 1, label: tr("15 床的簾子", "Bed 15 curtain") },
    { id: "kit", type: "supply", x: 8, y: 1, label: tr("處置車", "Procedure cart") },
    {
      id: "bed16",
      type: "bed",
      x: 2,
      y: 8,
      h: 2,
      pt: true,
      hair: "#2a2228",
      skin: "#efc6a0",
      sheet: "#e8e8f0",
      label: tr("16 床", "Bed 16"),
    },
    { id: "pail", type: "pail", x: 3, y: 9 },
    { id: "bed17", type: "bed", x: 7, y: 8, h: 2, label: tr("17 床（空床）", "Bed 17 (empty)") },
    { id: "counter", type: "counter", x: 13, y: 2, w: 4 },
    { id: "phone", type: "phone", x: 13, y: 2, label: tr("電話", "Phone") },
    { id: "lab", type: "computer", x: 15, y: 2, label: tr("急診電腦：檢驗", "ER computer: labs") },
    { id: "pacs", type: "pacs", x: 16, y: 2, label: tr("PACS：影像", "PACS: imaging") },
    { id: "board", type: "whiteboard", x: 18, y: 1, label: tr("急診白板", "ER whiteboard") },
    { id: "chairs", type: "chairs", x: 13, y: 9, w: 3 },
    { id: "water", type: "water", x: 18, y: 9 },
  ],
  furniture: {
    rail(c, px, py) {
      R(c, "#9aa0b2", px + 7, py, 2, 16);
      R(c, "#c9ced9", px + 5, py + 14, 6, 2);
    },
    supply(c, px, py) {
      obox(c, "#c9ced9", px + 1, py + 3, 14, 12);
      R(c, "#e8d890", px + 3, py + 5, 4, 3);
      R(c, "#9fd0ef", px + 9, py + 5, 4, 3);
      R(c, "#9aa0b2", px + 2, py + 10, 12, 1);
      R(c, "#1b1426", px + 2, py + 15, 2, 1);
      R(c, "#1b1426", px + 12, py + 15, 2, 1);
    },
    pacs(c, px, py) {
      R(c, "#20263a", px + 1, py - 2, 7, 7);
      R(c, "#20263a", px + 8, py - 2, 7, 7);
      R(c, "#9a9aa4", px + 2, py - 1, 5, 5);
      R(c, "#7a7a84", px + 9, py - 1, 5, 5);
      R(c, "#3a3f52", px + 7, py + 5, 2, 2);
    },
    pail(c, px, py) {
      R(c, "#7fb3d5", px + 5, py + 8, 6, 6);
      R(c, "#5f8fb8", px + 5, py + 8, 6, 1);
    },
  },
  labels: {
    bed: tr("15 床：秀琴阿嬤", "Bed 15: Grandma Hsiu-Chin"),
    nurse: tr("阿芬", "Fen"),
    er: tr("阿哲（急診 R）", "Che (ER resident)"),
    vs: tr("江主任", "Dr. Chiang"),
  },
  help: {
    mon: tr("生命徵象（會打開大螢幕）", "Vitals (opens the big screen)"),
    bed: tr("問診、身體檢查、放鼻胃管（會打開特寫）", "History, physical exam, NG tube (opens a close-up)"),
    curtain: tr("拉上或拉開 15 床的簾子", "Draw or open the Bed 15 curtain"),
    kit: tr("鼻胃管包、導尿管包", "NG tube kit, Foley kit"),
    bed16: tr("16 床：真的腸胃炎", "Bed 16: actual gastroenteritis"),
    bed17: tr("空床", "Empty bed"),
    phone: tr("打給江主任、阿凱、開刀房", "Call Dr. Chiang, Kai, the OR"),
    lab: tr("檢驗結果", "Lab results"),
    pacs: tr("KUB、CT（會打開片子）", "KUB, CT (opens the films)"),
    board: tr("急診白板", "ER whiteboard"),
    nurse: tr("開醫囑、請她陪同檢查", "Place orders, ask her to chaperone"),
    er: tr("照會你的急診 R", "The ER resident who consulted you"),
  },
  helpRules: tr(
    [
      "照會單上寫的診斷不一定是對的。",
      "私密部位的檢查：拉簾子、解釋並取得同意、找女性人員陪同。",
      "打給江主任要一句話講重點；你講得多急、多清楚，他就來得多快。",
    ],
    [
      "The diagnosis on the consult isn’t necessarily right.",
      "Intimate exams: draw the curtain, explain and get consent, have a female chaperone.",
      "When you call Dr. Chiang, get to the point in one sentence. The more urgent and clear you sound, the faster he comes.",
    ],
  ),
  player: [12, 6],
  ents: () => [
    mkEnt({ k: "afen", id: "afen", x: 14, y: 4, act: "nurse" }),
    mkEnt({ k: "er", id: "er", x: 17, y: 4, act: "er", face: "up" }),
  ],
  init: (): Vars => ({
    revealed: new Set(),
    orders: {},
    results: [],
    vitalsSeen: [],
    kub: new Set(),
    ctSeen: new Set(),
    curtain: false,
    chap: false,
    chapAsked: false,
    chapLeaveAt: null,
    consent: false,
    groinAt: null,
    rectal: false,
    taxisAt: null,
    taxisTold: false,
    erPushed: false,
    ngAt: null,
    ngBy: null,
    ng: null,
    headUp: false,
    aspAt: null,
    aspMild: false,
    aspActs: [],
    fluidAt: null,
    abxAt: null,
    ptLoc: "bed",
    ctLeave: null,
    ctBack: null,
    peritAt: null,
    lastVomit: 0,
    dauHere: false,
    explained: false,
    agreed: false,
    vsTalked: false,
    vsHinted: false,
    vsAccepted: false,
    vsAcceptedAt: null,
    vsArriveAt: null,
    vsHere: false,
    trip: null,
    vsCbAt: null,
    rCalled: false,
    rComingAt: null,
    rHere: false,
    dispoAt: null,
    bedAnim: null,
    bedGone: false,
    endAt: null,
  }),
  dialogs,
  zooms: { bed: bedZoom, pacs: () => kubZoom(false) },
  quick: {
    mon: openMon,
    curtain: () => {
      toggleCurtain();
      note(
        S.curtain
          ? tr("15 床的簾子拉上了。", "The Bed 15 curtain is drawn.")
          : tr("15 床的簾子拉開了。", "The Bed 15 curtain is open."),
      );
    },
    kit: () =>
      note(
        tr(
          "處置車：鼻胃管（16 Fr）、潤滑劑、灌食空針、膠布、引流袋、導尿管包。要放的話到床邊自己放，或請阿芬。",
          "Procedure cart: NG tube (16 Fr), lubricant, catheter-tip syringe, tape, drainage bag, Foley kit. To place one, do it yourself at the bedside or ask Fen.",
        ),
      ),
    bed16: () =>
      note(
        tr(
          "16 床的年輕人抱著嘔吐盆：「生蠔……我發誓再也不吃了……」這才是腸胃炎。",
          "The young guy in Bed 16 hugs his emesis basin: “Oysters… I swear I’m never eating them again…” Now that’s gastroenteritis.",
        ),
      ),
    bed17: () => note(tr("空床。", "Empty bed.")),
  },

  onStart: () =>
    eventBanner(tr("急診照會", "ER consult"), tr("15 床，78 歲，嘔吐、腹脹", "Bed 15, 78F, vomiting, distension")),
  beforeInteract(id) {
    if (id === "bed" && !atBed()) {
      note(tr("阿嬤去做 CT 了。", "Grandma is off at CT."));
      return false;
    }
    return true;
  },
  tick(t) {
    if (t === 1)
      ask(
        "er",
        "er",
        tr(
          "學弟！15 床陳秀琴，78 歲，吐了一整天、肚子脹，應該是 AGE 合併脫水啦，Cr 1.4。可是 KUB 有點怪，麻煩外科看一下，沒事我就收內科。",
          "Hey! Bed 15, Chen Hsiu-Chin, 78, vomiting all day, belly’s distended. Probably AGE with dehydration, Cr 1.4. But the KUB looks a bit off, so could surgery take a look? If it’s nothing, I’ll admit her to medicine.",
        ),
        {
          id: "consult",
          text: tr("阿哲在等你接照會", "Che is waiting to hand you a consult"),
          dlg: "er",
          clearOn: "open",
          icon: tr("！", "!"),
        },
      );
    if (t === 3)
      post(
        "bed",
        "npc",
        PT,
        tr(
          "歹勢啦醫生……這麼晚還麻煩你……我就吃壞肚子而已……",
          "Sorry, Doctor… troubling you so late… I just ate something bad, that’s all…",
        ),
      );
    for (const [id, o] of Object.entries(S.orders) as [OrderId, Order][]) if (!o.done && t >= o.ready) deliver(id);

    // Vomiting until decompressed
    if (S.ngAt === null && atBed() && t - S.lastVomit >= 11 && t > 5 && S.aspAt === null) {
      S.lastVomit = t;
      post(
        "bed",
        "narr",
        null,
        tr(
          "阿嬤又抱著嘔吐盆吐了，暗綠色的苦水，帶一點臭味。",
          "Grandma vomits into the basin again: bitter, dark green fluid with a faintly foul smell.",
        ),
      );
    }
    if (S.ngAt === null && S.aspAt === null && t === 34) aspirate();
    if (S.aspAt !== null && S.aspActs.length < 2 && t === S.aspAt + 4) {
      clearWait("asp");
      post(
        "bed",
        "alert",
        "afen",
        tr(
          "我先幫她抽了、氧氣接上！醫師你要顧呼吸道啊！",
          "I suctioned her and put O2 on! Doctor, you have to protect her airway!",
        ),
      );
      S.headUp = true;
      order("o2");
    }

    // CT trip
    if (S.ctLeave === t && atBed() && S.peritAt === null) {
      S.ptLoc = "ct";
      S.ctBack = t + 14;
      closeZoom();
      if (S.dialog === "bed") closeDialog();
      post("bed", "narr", null, tr("傳送人員把阿嬤推去 CT 室。", "Transport wheels Grandma off to CT."));
    }
    if (S.ctBack === t && S.ptLoc === "ct") {
      S.ptLoc = "bed";
      post("bed", "narr", null, tr("阿嬤做完 CT 被推回來。", "Grandma is wheeled back from CT."));
    }

    // Chaperone returns to her station
    if (S.chap && S.chapLeaveAt === t) {
      S.chap = false;
      S.chapAsked = false;
      walkEnt(ent("afen"), [14, 4]);
      post(
        "nurse",
        "npc",
        "afen",
        tr(
          "我先回去了，外面又來兩床。要再看的話再叫我。",
          "I’m heading back, two more patients just came in. Call me if you need to look again.",
        ),
      );
    }

    if (t === 22 && !S.dauHere) dauArrive();
    if (t === 14 && !S.vsTalked) {
      post(
        "r",
        "npc",
        "gs_r",
        tr(
          "（LINE）學弟，15 床看完直接打給江主任，我這台還要一陣子。",
          "(LINE) Once you’ve seen Bed 15, call Dr. Chiang directly. I’ll be stuck in this case a while.",
        ),
      );
      addWait({
        id: "vsline",
        text: tr("阿凱叫你看完打給江主任", "Kai wants you to call Dr. Chiang after you see her"),
        clearOn: "manual",
      });
    }

    // 阿哲 pushes taxis once the lump is found
    if (S.groinAt !== null && !S.erPushed && t >= S.groinAt + 2 && S.taxisAt === null && !S.vsHere) {
      S.erPushed = true;
      const e = ent("er");
      walkEnt(e, [5, 4]);
      e.onArrive = () => {
        e.face = "left";
      };
      ask(
        "er",
        "er",
        tr(
          "聽說是疝氣卡住？推回去就好啦，我以前在急診推過一堆。推回去她就不痛了，我收內科，你也可以去睡。",
          "I hear it’s a stuck hernia? Just push it back in. I’ve reduced tons of those in the ER. Push it back, the pain goes away, I admit her to medicine, and you get to go to bed.",
        ),
      );
    }
    if (S.erPushed && S.taxisAt === null && t === (S.groinAt ?? 0) + 10) walkEnt(ent("er"), [17, 4]);

    if (t === 28 && S.peritAt === null && !S.vsAccepted) {
      const v = vitals();
      S.vitalsSeen.push({ t, v });
      post(
        "nurse",
        "alert",
        "afen",
        tr(
          `醫師，15 床再量一次：${vtxt(v)}。她在發抖。`,
          `Doctor, repeat vitals on Bed 15: ${vtxt(v)}. She’s shivering.`,
        ),
      );
    }

    if (S.peritAt === null && t >= peritThreshold()) peritonitis();

    if (S.vsCbAt === t && !S.vsHere) {
      S.vsCbAt = null;
      incomingCall("cb");
    }
    if (S.vsArriveAt === t && !S.vsHere) {
      S.vsHere = true;
      pipSet(null);
      spawnAt("gs_vs", "vs", DOOR, [3, 4]);
      eventBanner(tr("江主任到場", "Dr. Chiang arrives"));
      S.endAt = t + 2;
    }
    if (S.rComingAt === t && !S.vsAccepted && !S.rHere) {
      S.rHere = true;
      spawnAt("gs_r", "r", DOOR, [3, 4]);
      eventBanner(tr("阿凱下來了", "Kai comes down"));
      post(
        "bed",
        "npc",
        "gs_r",
        S.groinAt !== null
          ? tr(
              "我看一下。……鼠蹊部紅成這樣，你怎麼沒打給主任？我打。",
              "Let me see. …The groin’s this red and you didn’t call the attending? I’ll call.",
            )
          : tr(
              "我看一下。……沒開過刀的 SBO，鼠蹊部看過了嗎？……你看，這裡。我打給主任。",
              "Let me see. …SBO with no prior surgery. Did you check the groin? …Look, right here. I’m calling the attending.",
            ),
      );
      S.endAt = t + 3;
    }
    if (S.endAt === t) {
      if (S.dispoAt !== null) endD();
      else finale(S.vsHere ? "gs_vs" : "gs_r");
    }
  },
  timeup: () =>
    void playEnding(
      "Z",
      tr("交班", "Sign-out"),
      [
        {
          who: "gs_r",
          text: tr(
            "學弟，我開完了。15 床我來，你把剛剛的東西跟我交班。",
            "I’m out of the OR. I’ll take Bed 15. Sign out to me on everything you’ve done.",
          ),
        },
      ],
      tr("時間到，阿凱接手。", "Time’s up. Kai takes over."),
    ),

  hud() {
    const last = S.vitalsSeen.at(-1);
    const lac = S.results.find((x) => x.text.startsWith("VBG"));
    return [
      [
        last ? tr(`${clock(last.t)} 生命徵象`, `Vitals ${clock(last.t)}`) : tr("生命徵象", "Vitals"),
        last
          ? tr(
              `${last.v.sbp}/${last.v.dbp}　HR ${last.v.hr}　${last.v.temp.toFixed(1)}°`,
              `${last.v.sbp}/${last.v.dbp} · HR ${last.v.hr} · ${last.v.temp.toFixed(1)}°`,
            )
          : tr("分診 138/80　HR 102", "Triage 138/80 · HR 102"),
      ],
      ["NG", S.ngAt !== null ? `${ngOut()} mL` : tr("沒放", "None")],
      [
        "Lactate",
        lac
          ? (/Lactate ([\d.]+)/.exec(lac.text)?.[1] ?? "—")
          : S.orders.lactate
            ? tr("等待中", "Pending")
            : tr("沒驗", "Not sent"),
      ],
    ];
  },
  urgent: () => S.peritAt !== null || (S.aspAt !== null && S.t - S.aspAt < 10) || S.t >= 45,
  outcomeText: () =>
    S.vsAccepted ? tr("江主任接手，排急刀", "Dr. Chiang takes over; emergency case booked") : tr("結束", "Over"),
  drawBelow(c, now) {
    // Monitor on its stand
    const mx = 1 * T;
    const my = 2 * T;
    c.fillStyle = "#8a90a2";
    c.fillRect(mx + 7, my + 6, 2, 10);
    c.fillStyle = "#3a3f52";
    c.fillRect(mx + 1, my, 14, 9);
    c.fillStyle = "#0f1f1a";
    c.fillRect(mx + 2, my + 1, 12, 7);
    const v = vitals();
    c.fillStyle = v.spo2 < 92 || v.hr > 118 ? "#ff5a6a" : "#5be38a";
    for (let i = 0; i < 12; i++) {
      const ph = (i + Math.floor(now / (v.hr > 115 ? 60 : 100))) % 12;
      c.fillRect(mx + 2 + i, ph === 3 ? my + 2 : ph === 4 ? my + 6 : my + 4, 1, 1);
    }
    // 15 床 (wheeled out in the endings)
    if (!S.bedGone) {
      const p = bedAnimPos(S.bedAnim, now, [2, 2]);
      if (!p) S.bedGone = true;
      else {
        const bx = Math.round(p[0] * T);
        const by = Math.round(p[1] * T);
        DRAW.bed?.(
          c,
          bx,
          by,
          atBed()
            ? {
                pt: true,
                hair: "#d8d8de",
                skin: S.peritAt !== null ? "#e8d8cc" : "#e0b088",
                sheet: S.headUp ? "#cfe0f0" : "#e8e8f0",
              }
            : {},
        );
        if (atBed() && S.ngAt !== null) R(c, "#e8d890", bx + 10, by + 6, 1, 6);
        if (atBed() && S.ngAt === null && S.t > 5 && S.aspAt === null && Math.floor(now / 900) % 3 === 0)
          R(c, "#7a9a4a", bx + 12, by + 8, 2, 2);
      }
    }
    // Curtain around the bay
    if (S.curtain) {
      for (let y = 1 * T; y < 5 * T; y += 4) {
        R(c, "#9fc8b8", 5 * T + 12, y, 3, 4);
        R(c, "#86b0a0", 5 * T + 12, y + 3, 3, 1);
      }
      for (let x = 1 * T; x < 5 * T + 12; x += 4) {
        R(c, "#9fc8b8", x, 5 * T - 3, 4, 3);
        R(c, "#86b0a0", x + 3, 5 * T - 3, 1, 3);
      }
    } else {
      R(c, "#9fc8b8", 5 * T + 11, 1 * T, 5, 8);
      R(c, "#86b0a0", 5 * T + 11, 1 * T + 8, 5, 2);
    }
  },
  drawAbove(c) {
    R(c, "#9aa0b2", 1 * T, 1 * T - 1, 5 * T, 1);
    R(c, "#9aa0b2", 6 * T - 1, 1 * T - 1, 1, 4 * T);
  },

  graderCase: tr(
    `病例：一般外科 clerk 被急診照會。78 歲女性，嘔吐一天、腹脹、沒有排氣排便，急診認為是腸胃炎合併脫水（Cr 1.4、K 3.2、WBC 13,800）。KUB：小腸擴張、階梯狀氣液面、大腸少氣。真相：右側股疝氣嵌頓已經絞扼，造成小腸阻塞；她從沒開過刀（沒開過刀的 SBO 一定要檢查疝氣孔）。阿嬤覺得丟臉，不會主動說大腿根有一顆二十年的「肉球」，昨天開始推不回去；女兒（約 22 分鐘後到）知道。鼠蹊部檢查需要拉簾子、解釋並取得同意、請女性護理師陪同。團塊紅、熱、壓痛、推不回去＝絞扼徵象，不能徒手推回（taxis）；急診 R 會建議推回去。正確處置：禁食、NG 減壓（沒放 NG 的話約 34 分鐘時會嘔吐嗆到；床頭搖高可減輕）、輸液與補鉀、Foley、lactate、備血、抗生素，打電話向江主任報告（一句話講重點，沒檢查鼠蹊部主任會反問），排緊急手術並跟病人、女兒說明。約 58 分鐘（若推回去則推後 16 分鐘）出現腹膜炎。主治趕來的速度取決於 clerk 電話裡傳達的急迫性。`,
    `Case: a general surgery clerk is consulted by the ER. 78-year-old woman with a day of vomiting, abdominal distension, no flatus or stool; the ER thinks it's gastroenteritis with dehydration (Cr 1.4, K 3.2, WBC 13,800). KUB: dilated small bowel, stepladder air-fluid levels, little colonic gas. Truth: an incarcerated right femoral hernia that has already strangulated, causing a small bowel obstruction; she has never had surgery (an SBO with no prior surgery always requires checking the hernia orifices). Grandma is embarrassed and will not volunteer that she has had a "lump of flesh" at the top of her thigh for twenty years that stopped going back in yesterday; her daughter (arrives at about 22 minutes) knows. The groin exam requires drawing the curtain, explaining and getting consent, and having a female nurse chaperone. A red, hot, tender, irreducible lump = signs of strangulation; it must not be manually reduced (taxis); the ER resident will suggest pushing it back. Correct management: NPO, NG decompression (without an NG she vomits and aspirates at about 34 minutes; raising the head of the bed lessens it), IV fluids and potassium replacement, Foley, lactate, crossmatch, antibiotics; phone Dr. Chiang (one-sentence presentation; if the groin wasn't examined he asks about it), book emergency surgery and explain to the patient and daughter. Peritonitis develops at about 58 minutes (or 16 minutes after a manual reduction). How fast the attending arrives depends on the urgency the clerk conveyed on the phone.`,
  ),
  rubric: tr(
    [
      "小腸阻塞的判斷：嘔吐性質、沒排氣排便、腹脹、腸音、KUB",
      "找病因：問開刀史，檢查疝氣孔，問出（或從女兒問出）鼠蹊部團塊",
      "私密檢查的隱私與尊重：拉簾子、解釋與同意、女性陪同",
      "辨認絞扼徵象，拒絕徒手推回（taxis）",
      "初步處置：禁食、NG 減壓、輸液補鉀、Foley、lactate、備血、抗生素；預防與處理吸入",
      "向江主任報告：時機、重點、急迫性",
      "與阿嬤和女兒溝通：為什麼要開刀、可能切腸",
    ],
    [
      "Recognizing SBO: character of the vomiting, no flatus or stool, distension, bowel sounds, KUB",
      "Finding the cause: surgical history, examining the hernia orifices, eliciting the groin lump (or getting it from the daughter)",
      "Privacy and respect for an intimate exam: curtain, explanation and consent, female chaperone",
      "Recognizing signs of strangulation and refusing manual reduction (taxis)",
      "Initial management: NPO, NG decompression, IV fluids with potassium, Foley, lactate, crossmatch, antibiotics; preventing and managing aspiration",
      "Presenting to Dr. Chiang: timing, key points, urgency",
      "Communicating with Grandma and her daughter: why surgery is needed, possible bowel resection",
    ],
  ),
  facts: () => [
    tr(
      `鼠蹊部團塊：${S.groinAt !== null ? clock(S.groinAt) : "沒有找到"}`,
      `Groin lump: ${S.groinAt !== null ? clock(S.groinAt) : "not found"}`,
    ),
    tr(
      `隱私：簾子${S.curtain ? "有拉" : "沒拉"}，陪同${S.chap || S.chapAsked ? "有" : "沒有"}，同意${S.consent ? "有" : "沒有"}`,
      `Privacy: curtain ${S.curtain ? "drawn" : "not drawn"}, chaperone ${S.chap || S.chapAsked ? "yes" : "no"}, consent ${S.consent ? "yes" : "no"}`,
    ),
    tr(
      `徒手推回：${S.taxisAt !== null ? `有（${clock(S.taxisAt)}）${S.taxisTold ? "，有告訴主治" : "，沒有告訴主治"}` : "沒有"}`,
      `Manual reduction: ${S.taxisAt !== null ? `yes (${clock(S.taxisAt)})${S.taxisTold ? ", told the attending" : ", did not tell the attending"}` : "no"}`,
    ),
    tr(
      `NG：${S.ngAt !== null ? `${clock(S.ngAt)}（${S.ngBy === "self" ? "自己放" : "阿芬放"}）` : "沒有"}`,
      `NG: ${S.ngAt !== null ? `${clock(S.ngAt)} (${S.ngBy === "self" ? "placed by the clerk" : "placed by Fen"})` : "none"}`,
    ),
    tr(
      `輸液：${S.fluidAt !== null ? clock(S.fluidAt) : "沒有加快"}；補鉀：${S.orders.kcl ? "有" : "沒有"}`,
      `IV fluids: ${S.fluidAt !== null ? clock(S.fluidAt) : "not increased"}; potassium: ${S.orders.kcl ? "yes" : "no"}`,
    ),
    tr(
      `抗生素：${S.abxAt !== null ? clock(S.abxAt) : "沒有"}；備血：${S.orders.ts ? "有" : "沒有"}；lactate：${S.orders.lactate ? "有" : "沒有"}`,
      `Antibiotics: ${S.abxAt !== null ? clock(S.abxAt) : "none"}; crossmatch: ${S.orders.ts ? "yes" : "no"}; lactate: ${S.orders.lactate ? "yes" : "no"}`,
    ),
    tr(
      `嘔吐嗆到：${S.aspAt !== null ? clock(S.aspAt) : "沒有"}`,
      `Aspiration: ${S.aspAt !== null ? clock(S.aspAt) : "none"}`,
    ),
    tr(
      `報告江主任：${S.vsAcceptedAt !== null ? `${clock(S.vsAcceptedAt)}（${S.trip ? { high: "很急", medium: "普通", low: "不急" }[S.trip.urg] : "—"}）` : "沒有成功"}`,
      `Presented to Dr. Chiang: ${S.vsAcceptedAt !== null ? `${clock(S.vsAcceptedAt)} (${S.trip ? { high: "very urgent", medium: "moderate", low: "not urgent" }[S.trip.urg] : "—"})` : "not successfully"}`,
    ),
    tr(
      `腹膜炎：${S.peritAt !== null ? clock(S.peritAt) : "沒有"}`,
      `Peritonitis: ${S.peritAt !== null ? clock(S.peritAt) : "none"}`,
    ),
    tr(
      `跟女兒說明：${S.explained ? "有" : "沒有"}；阿嬤同意開刀：${S.agreed ? "有" : "沒有"}`,
      `Explained to daughter: ${S.explained ? "yes" : "no"}; Grandma consented to surgery: ${S.agreed ? "yes" : "no"}`,
    ),
  ],
  known,
};
